import React, { useEffect, useState, useRef } from "react";
import {
  FaMapMarked,
  FaBed,
  FaShareAlt,
  FaRegBookmark,
  FaBookmark,
  FaMapMarker,
  FaCameraRetro,
} from "react-icons/fa";
import { GoHome } from "react-icons/go";
import { IoDocumentTextOutline } from "react-icons/io5";
import { LuUserSquare2 } from "react-icons/lu";
import {
  MdOutlineCalendarMonth,
  MdOutlinePhone,
  MdNoteAlt,
} from "react-icons/md";
import { RxDimensions } from "react-icons/rx";
import { TbLamp } from "react-icons/tb";
import Cookies from "js-cookie";
import apiClient from "../common/apiClient";
import Photos_Video from "./Photos_Video";
import {
  Building,
  Home,
  MapPin,
  Calendar,
  Phone,
  Eye,
  CreditCard as Edit3,
  Trash2,
  Plus,
  Share2,
  Bookmark,
  Play,
} from "lucide-react";
import { FaCopy, FaUserCircle } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";


const formatPriceRange = (priceRange) => {
  if (!priceRange) return "Price On Request";

  const min = priceRange.min;
  const max = priceRange.max;

  // No values
  if (!min && !max) return "Price On Request";

  // Only min
  if (min && !max) return formatPrice(min);

  // Only max
  if (!min && max) return formatPrice(max);

  // Both same
  if (min === max) return formatPrice(min);

  // Both exist
  return `${formatPrice(min)} - ${formatPrice(max)}`;
};

// Format price in lacs and crores
const formatPrice = (price) => {
  if (!price || price === "0") return "Price On Request";
  const numPrice = Number(price);

  if (numPrice >= 10000000) {
    // 1 crore or more
    const croreValue = numPrice / 10000000;
    return `₹${croreValue % 1 === 0 ? croreValue.toFixed(0) : croreValue.toFixed(2)}Cr`;
  } else if (numPrice >= 100000) {
    // 1 lac or more
    const lacValue = numPrice / 100000;
    return `₹${lacValue % 1 === 0 ? lacValue.toFixed(0) : lacValue.toFixed(2)}Lac`;
  } else {
    return `₹${numPrice}`;
  }
}

// BUILDER PRICE HANDLING
const getBuilderPrice = (property) => {
  if (!property?.priceRange) return "Price On Request";

  const min = property.priceRange?.min;
  const max = property.priceRange?.max;

  // No values
  if (!min && !max) return "Price On Request";

  // Only min
  if (min && !max) return formatPrice(min);

  // Only max
  if (!min && max) return formatPrice(max);

  // Both present
  if (min === max) {
    return formatPrice(min);
  }

  return `${formatPrice(min)} - ${formatPrice(max)}`;
};



// Custom Dropdown Component (similar to your property card)
const StatusDropdown = ({ status, onChange, propertyType, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // If custom options are provided (e.g., for requirements), use them; otherwise fall back to property options
  const statusOptions = options ?? [
    { value: "Available", label: "Available", color: "bg-green-500" },
    { value: "Rented", label: "Rented", color: "bg-yellow-500" },
    { value: "Sold", label: "Sold", color: "bg-red-500" },
    { value: "Fake Listing", label: "Fake Listing", color: "bg-gray-500" },
    {
      value: "Rented On PRObroker",
      label: "Rented On PRObroker",
      color: "bg-blue-500",
    },
    {
      value: "Rented via Other",
      label: "Rented via Other",
      color: "bg-orange-500",
    },
    {
      value: "Sold On PRObroker",
      label: "Sold On PRObroker",
      color: "bg-purple-500",
    },
    { value: "Sold Via Other", label: "Sold Via Other", color: "bg-pink-500" },
  ];

  const getStatusClassName = (statusValue) => {
    const option = statusOptions.find((opt) => opt.value === statusValue);
    return option ? option.color : "bg-gray-500";
  };

  const getStatusDisplay = (statusValue) => {
    const option = statusOptions.find((opt) => opt.value === statusValue);
    return option ? option.label : "Available";
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleStatusChange = (newStatus) => {
    onChange(newStatus);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer transition-all duration-300 border
    ${status === "Active" || status === "Available"
            ? "bg-gradient-to-r from-green-500/10 to-emerald-500/10 text-green-700 border-green-200"
            : status === "Fulfilled"
              ? "bg-gradient-to-r from-blue-500/10 to-cyan-500/10 text-blue-700 border-blue-200"
              : status === "Cancelled"
                ? "bg-gradient-to-r from-red-500/10 to-rose-500/10 text-red-700 border-red-200"
                : `${getStatusClassName(status)} text-white border-transparent`
          }`}
        onClick={() => setIsOpen(!isOpen)}
      >


        <span className="text-sm font-medium">{getStatusDisplay(status)}</span>
        <svg
          className={`w-4 h-4 transform transition-transform ${isOpen ? "rotate-180" : ""
            }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {isOpen && (
        <div className="custom-left-offset mt-1 w-32 bg-white border  rounded-lg shadow-lg z-10 left-45">
          {statusOptions.map((option) => (
            <div
              key={option.value}
              className={`px-4 py-2 cursor-pointer hover:bg-gray-200 text-nowrap ${status === option.value ? "bg-gray-50" : ""
                }`}
              onClick={() => handleStatusChange(option.value)}
            >
              <span className="text-base text-gray-700">{option.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Toast Notification Component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div
      className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg ${type === "success" ? "bg-green-500 text-white" : "bg-red-500 text-white"
        }`}
    >
      {message}
    </div>
  );
};

// Confirmation Modal Component
const ConfirmationModal = ({ isOpen, onClose, onConfirm, status }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
        <h3 className="text-lg font-semibold mb-4">Confirm Status Change</h3>
        <p className="text-gray-600 mb-6">
          Are you sure you want to change the status to "{status}"?
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

// Delete Confirmation Modal Component
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
        <h3 className="text-lg font-semibold mb-4">Confirm Delete</h3>
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete this property? This action cannot be
          undone.
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const MyProperty = () => {
  const location = useLocation();
  const [properties, setProperties] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [builderProperties, setBuilderProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [filteredRequirements, setFilteredRequirements] = useState([]);
  const [filteredBuilderProperties, setFilteredBuilderProperties] = useState(
    []
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // You can adjust this number

  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("properties");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedBuilderCategory, setSelectedBuilderCategory] = useState("all");
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();

  const lastFetchedTab = useRef("");

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  useEffect(() => {
    // Scroll to top when page changes
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, selectedCategory, selectedBuilderCategory]);

  // Get current items based on active tab
  const getCurrentItems = () => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;

    switch (activeTab) {
      case "properties":
        return filteredProperties.slice(indexOfFirstItem, indexOfLastItem);
      case "requirements":
        return filteredRequirements.slice(indexOfFirstItem, indexOfLastItem);
      case "builder":
        return filteredBuilderProperties.slice(indexOfFirstItem, indexOfLastItem);
      default:
        return [];
    }
  };

  // Get total pages based on active tab
  const getTotalPages = () => {
    let totalItems = 0;
    switch (activeTab) {
      case "properties":
        totalItems = filteredProperties.length;
        break;
      case "requirements":
        totalItems = filteredRequirements.length;
        break;
      case "builder":
        totalItems = filteredBuilderProperties.length;
        break;
      default:
        totalItems = 0;
    }
    return Math.ceil(totalItems / itemsPerPage);
  };

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Next page
  const nextPage = () => {
    if (currentPage < getTotalPages()) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Previous page
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Pagination Component
  const Pagination = () => {
    const totalPages = getTotalPages();
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex justify-center items-center mt-8 space-x-2">
        {/* Previous Button */}
        <button
          onClick={prevPage}
          disabled={currentPage === 1}
          className={`px-3 py-2 rounded-lg border ${currentPage === 1
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
            }`}
        >
          Previous
        </button>

        {/* First Page */}
        {startPage > 1 && (
          <>
            <button
              onClick={() => paginate(1)}
              className={`px-3 py-2 rounded-lg border ${1 === currentPage
                ? "bg-[#503691] text-white border-[#503691]"
                : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
                }`}
            >
              1
            </button>
            {startPage > 2 && (
              <span className="px-2 text-gray-500">...</span>
            )}
          </>
        )}

        {/* Page Numbers */}
        {pageNumbers.map(number => (
          <button
            key={number}
            onClick={() => paginate(number)}
            className={`px-3 py-2 rounded-lg border ${number === currentPage
              ? "bg-[#503691] text-white border-[#503691]"
              : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
              }`}
          >
            {number}
          </button>
        ))}

        {/* Last Page */}
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && (
              <span className="px-2 text-gray-500">...</span>
            )}
            <button
              onClick={() => paginate(totalPages)}
              className={`px-3 py-2 rounded-lg border ${totalPages === currentPage
                ? "bg-[#503691] text-white border-[#503691]"
                : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
                }`}
            >
              {totalPages}
            </button>
          </>
        )}

        {/* Next Button */}
        <button
          onClick={nextPage}
          disabled={currentPage === totalPages}
          className={`px-3 py-2 rounded-lg border ${currentPage === totalPages
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
            }`}
        >
          Next
        </button>
      </div>
    );
  };


  const categoryFilters = [
    {
      id: "residential-rent",
      label: "Residential Rent",
      icon: Home,
      color: "bg-orange-100 text-orange-600 border-orange-200",
    },
    {
      id: "commercial-rent",
      label: "Commercial Rent",
      icon: Building,
      color: "bg-blue-100 text-blue-600 border-blue-200",
    },
    {
      id: "residential-sell",
      label: "Residential Sell",
      icon: Home,
      color: "bg-green-100 text-green-600 border-green-200",
    },
    {
      id: "commercial-sell",
      label: "Commercial Sell",
      icon: Building,
      color: "bg-purple-100 text-purple-600 border-purple-200",
    },
  ];

  const builderCategoryFilters = [
    {
      id: "all",
      label: "All",
      icon: Building,
    },
    {
      id: "residential",
      label: "Residential",
      icon: Home,
    },
    {
      id: "commercial",
      label: "Commercial",
      icon: Building,
    },
  ];

  useEffect(() => {
    if (selectedCategory === "all") {
      setFilteredProperties(properties);
      setFilteredRequirements(requirements);
    } else {
      // Filter properties
      const filteredProps = properties.filter((property) => {
        switch (selectedCategory) {
          case "residential-rent":
            return property?.type === "Residential Rent";
          case "commercial-rent":
            return property?.type === "Commercial Rent";
          case "residential-sell":
            return property?.type === "Residential Sell";
          case "commercial-sell":
            return property?.type === "Commercial Sell";
          default:
            return true;
        }
      });
      setFilteredProperties(filteredProps);

      // Filter requirements (assuming requirements have similar type structure)
      const filteredReqs = requirements.filter((requirement) => {
        switch (selectedCategory) {
          case "residential-rent":
            return (
              requirement?.type === "Residential Rent" ||
              requirement?.type === "residential-rent"
            );
          case "commercial-rent":
            return (
              requirement?.type === "Commercial Rent" ||
              requirement?.type === "commercial-rent"
            );
          case "residential-sell":
            return (
              requirement?.type === "Residential Sell" ||
              requirement?.type === "residential-sell"
            );
          case "commercial-sell":
            return (
              requirement?.type === "Commercial Sell" ||
              requirement?.type === "commercial-sell"
            );
          default:
            return true;
        }
      });
      setFilteredRequirements(filteredReqs);
    }
  }, [selectedCategory, properties, requirements]);

  useEffect(() => {
    if (selectedBuilderCategory === "all") {
      setFilteredBuilderProperties(builderProperties);
    } else {
      const filteredBuilderProps = builderProperties.filter((property) => {
        switch (selectedBuilderCategory) {
          case "residential":
            return property?.type?.includes("Residential");
          case "commercial":
            return property?.type?.includes("Commercial");
          default:
            return true;
        }
      });
      setFilteredBuilderProperties(filteredBuilderProps);
    }
  }, [selectedBuilderCategory, builderProperties]);

  const fetchMyProperties = async () => {
    const token = Cookies.get("token");
    try {
      setLoading(true);
      const result = await apiClient(
        "GET",
        "/cjidnvij/ceksfbuebijn/user/brokerproperties/my-properties?listedBy=broker",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!result.success) {
        throw new Error(result.error || "Failed to fetch properties");
      }

      const data = result.data;
      if (data?.success) {
        setProperties(data.data || []);
        setFilteredProperties(data.data || []);
        // The same endpoint also returns the broker's requirements
        setRequirements(data.requirements || []);
        setFilteredRequirements(data.requirements || []);
      } else {
        setProperties(data || []);
        setFilteredProperties(data || []);
      }
    } catch (error) {
      console.error("Error fetching properties:", error);
      setError("Failed to fetch properties");
    } finally {
      setLoading(false);
    }
  };

  // Add function to fetch requirements (you'll need to implement this based on your API)
  const fetchMyRequirements = async () => {
    const token = Cookies.get("token");
    try {
      // Replace with your actual requirements API endpoint
      const result = await apiClient(
        "GET",
        "/cjidnvij/ceksfbuebijn/user/brokerrequirements/my-requirements", // Update this endpoint
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!result.success) {
        throw new Error(result.error || "Failed to fetch requirements");
      }

      const data = result.data;
      if (data?.success) {
        setRequirements(data.data || []);
        setFilteredRequirements(data.data || []);
      } else {
        setRequirements(data || []);
        setFilteredRequirements(data || []);
      }
    } catch (error) {
      console.error("Error fetching requirements:", error);
      // Don't set error state for requirements to avoid breaking the properties display
    }
  };

  // Add function to fetch builder properties
  const fetchBuilderProperties = async () => {
    const token = Cookies.get("token");
    try {
      // Replace with your actual builder properties API endpoint
      const result = await apiClient(
        "GET",
        "/cjidnvij/ceksfbuebijn/user/brokerproperties/my-properties?listedBy=builders", // Update this endpoint
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!result.success) {
        throw new Error(result.error || "Failed to fetch builder properties");
      }

      const data = result.data;
      if (data?.success) {
        setBuilderProperties(data.data || []);
        setFilteredBuilderProperties(data.data || []);
      } else {
        setBuilderProperties(data || []);
        setFilteredBuilderProperties(data || []);
      }
    } catch (error) {
      console.error("Error fetching builder properties:", error);
      // Don't set error state for builder properties to avoid breaking the display
    }
  };

  useEffect(() => {
    if (activeTab === lastFetchedTab.current) return;
    lastFetchedTab.current = activeTab;

    if (activeTab === "properties") {
      fetchMyProperties();
    } else if (activeTab === "requirements") {
      fetchMyRequirements();
    } else if (activeTab === "builder") {
      fetchBuilderProperties();
    }
  }, [activeTab]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Loading properties...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  if (
    properties.length === 0 &&
    requirements.length === 0 &&
    builderProperties.length === 0
  ) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">No properties found</div>
      </div>
    );
  }
  const showToast = (message) => {
    const toast = document.createElement("div");
    toast.innerText = message;
    toast.style.position = "fixed";
    toast.style.bottom = "20px";
    toast.style.left = "50%";
    toast.style.transform = "translateX(-50%)";
    toast.style.backgroundColor = "#333";
    toast.style.color = "#fff";
    toast.style.padding = "10px 20px";
    toast.style.borderRadius = "5px";
    toast.style.zIndex = "1000";
    document.body.appendChild(toast);

    setTimeout(() => {
      document.body.removeChild(toast);
    }, 3000);
  };

  const handleCopyProfileLink = () => {
    const brokerId = Cookies.get("bid"); // Replace with actual broker ID from your data
    const profileLink = `https://probroker.in/broker-profile/${brokerId}`;

    navigator.clipboard
      .writeText(profileLink)
      .then(() => {
        showToast("Content copied to clipboard!");
      })
      .catch(() => {
        setToast({ message: "Failed to copy link", type: "error" });
      });
  };

  const handleNavigateToProfile = () => {
    const brokerId = Cookies.get("bid"); // Get brokerId from cookies

    if (!brokerId) {
      setToast({ message: "Broker ID not found", type: "error" });
      return;
    }

    navigate(`/broker-profile/${encodeURIComponent(brokerId)}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Centered Tab Navigation */}

        <div className="flex justify-between items-center mb-10 relative">
          {/* Left side - Empty space for balance */}
          <div className="w-0 md:w-auto"></div>

          {/* Center - Tab Navigation (unchanged position) */}
          <div className="absolute left-1/2 transform -translate-x-1/2">
            <div className="bg-white rounded-full p-1 shadow-sm border border-gray-200 w-auto">
              <div className="flex">
                <button
                  onClick={() => setActiveTab("properties")}
                  className={`px-6 py-3 rounded-full font-medium transition-all duration-300 whitespace-nowrap ${activeTab === "properties"
                    ? "bg-[#503691] text-white shadow-md"
                    : "text-gray-600 hover:text-[#503691]"
                    }`}
                >
                  Properties
                </button>
                {/* <button
                  onClick={() => setActiveTab("requirements")}
                  className={`px-6 py-3 rounded-full font-medium transition-all duration-300 whitespace-nowrap ${activeTab === "requirements"
                    ? "bg-[#503691] text-white shadow-md"
                    : "text-gray-600 hover:text-[#503691]"
                    }`}
                >
                  Requirements
                </button> */}
                {/* <button
                  onClick={() => setActiveTab("builder")}
                  className={`px-6 py-3 rounded-full font-medium transition-all duration-300 whitespace-nowrap ${activeTab === "builder"
                    ? "bg-[#503691] text-white shadow-md"
                    : "text-gray-600 hover:text-[#503691]"
                    }`}
                >
                  Builder
                </button> */}
              </div>
            </div>
          </div>

          {/* Right side - Profile Sharing Buttons (Hidden on mobile) */}
          <div className="hidden md:flex gap-3">
            <button
              onClick={handleCopyProfileLink}
              className="flex items-center gap-2 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
              title="Copy Profile Link"
            >
              <FaCopy className="h-4 w-4" />
            </button>
            <button
              onClick={handleNavigateToProfile}
              className="flex items-center gap-2 bg-[#503691] text-white px-4 py-2 rounded-lg hover:bg-[#3d2a6d] transition-colors"
            >
              <FaUserCircle className="h-4 w-4" />
              My Profile Sharing
            </button>
          </div>
        </div>

        {/* Mobile Profile Buttons - Bottom right of tabs */}
        <div className="md:hidden flex justify-end mt-6 mb-6">
          <div className="flex gap-3">
            <button
              onClick={handleCopyProfileLink}
              className="flex items-center gap-2 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
              title="Copy Profile Link"
            >
              <FaCopy className="h-4 w-4" />
            </button>
            <button
              onClick={handleNavigateToProfile}
              className="flex items-center gap-2 bg-[#503691] text-white px-4 py-2 rounded-lg hover:bg-[#3d2a6d] transition-colors"
            >
              <FaUserCircle className="h-4 w-4" />
              <span className=" sm:inline">My Profile Sharing</span>
            </button>
          </div>
        </div>

        {/* Category Stats Boxes */}
        <div className="">
          {activeTab === "builder" ? (
            // Builder Category Filters
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {builderCategoryFilters.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedBuilderCategory(category.id)}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 ${selectedBuilderCategory === category.id
                    ? "border-[#503691] bg-purple-50"
                    : "border-gray-200 bg-white hover:border-purple-300 hover:shadow-md"
                    }`}
                >
                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br rounded-full flex items-center justify-center">
                      {category.id === "all" && (
                        <img src="./image/5.png" alt="All Icon" />
                      )}
                      {category.id === "residential" && (
                        <img src="./image/2.png" alt="Residential Icon" />
                      )}
                      {category.id === "commercial" && (
                        <img src="./image/4.png" alt="Commercial Icon" />
                      )}
                    </div>
                    <div className="text-sm font-medium text-gray-600">
                      {category.label}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {category.id === "all" && `(${builderProperties.length})`}
                      {category.id === "residential" &&
                        `(${builderProperties.filter((p) =>
                          p?.type?.includes("Residential")
                        ).length
                        })`}
                      {category.id === "commercial" &&
                        `(${builderProperties.filter((p) =>
                          p?.type?.includes("Commercial")
                        ).length
                        })`}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            // Original Category Filters for Properties and Requirements
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-6xl mx-auto">
              {/* All Categories */}
              <button
                onClick={() => setSelectedCategory("all")}
                className={`p-4 rounded-xl border-2 transition-all duration-300 ${selectedCategory === "all"
                  ? "border-[#503691] bg-purple-50"
                  : "border-gray-200 bg-white hover:border-purple-300 hover:shadow-md"
                  }`}
              >
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br rounded-full flex items-center justify-center">
                    <img src="./image/5.png" alt="Property Icon" />
                  </div>
                  <div className="text-sm font-medium text-gray-600">
                    All {activeTab === "properties" ? "Properties" : "Requirements"}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    ({activeTab === "properties" ? properties.length : requirements.length})
                  </div>
                </div>
              </button>

              <button
                onClick={() => setSelectedCategory("residential-rent")}
                className={`p-4 rounded-xl border-2 transition-all duration-300 ${selectedCategory === "residential-rent"
                  ? "border-[#503691] bg-purple-50"
                  : "border-gray-200 bg-white hover:border-purple-300 hover:shadow-md"
                  }`}
              >
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br rounded-full flex items-center justify-center">
                    <img src="./image/residental-rent.png" alt="Property Icon" />
                  </div>
                  <div className="text-sm font-medium text-gray-600">
                    Residential Rent
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    (
                    {activeTab === "properties"
                      ? properties.filter(p => p?.type === "Residential Rent").length
                      : requirements.filter(r =>
                        r?.type === "Residential Rent" || r?.type === "residential-rent"
                      ).length
                    }
                    )
                  </div>
                </div>
              </button>

              <button
                onClick={() => setSelectedCategory("residential-sell")}
                className={`p-4 rounded-xl border-2 transition-all duration-300 ${selectedCategory === "residential-sell"
                  ? "border-[#503691] bg-purple-50"
                  : "border-gray-200 bg-white hover:border-purple-300 hover:shadow-md"
                  }`}
              >
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br rounded-full flex items-center justify-center">
                    <img src="./image/2.png" alt="Property Icon" />
                  </div>
                  <div className="text-sm font-medium text-gray-600">
                    Residential Sell
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    (
                    {activeTab === "properties"
                      ? properties.filter(p => p?.type === "Residential Sell").length
                      : requirements.filter(r =>
                        r?.type === "Residential Sell" || r?.type === "residential-sell"
                      ).length
                    }
                    )
                  </div>
                </div>
              </button>

              <button
                onClick={() => setSelectedCategory("commercial-rent")}
                className={`p-4 rounded-xl border-2 transition-all duration-300 ${selectedCategory === "commercial-rent"
                  ? "border-[#503691] bg-purple-50"
                  : "border-gray-200 bg-white hover:border-purple-300 hover:shadow-md"
                  }`}
              >
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br rounded-full flex items-center justify-center">
                    <img src="./image/3.png" alt="Property Icon" />
                  </div>
                  <div className="text-sm font-medium text-gray-600">
                    Commercial Rent
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    (
                    {activeTab === "properties"
                      ? properties.filter(p => p?.type === "Commercial Rent").length
                      : requirements.filter(r =>
                        r?.type === "Commercial Rent" || r?.type === "commercial-rent"
                      ).length
                    }
                    )
                  </div>
                </div>
              </button>

              <button
                onClick={() => setSelectedCategory("commercial-sell")}
                className={`p-4 rounded-xl border-2 transition-all duration-300 ${selectedCategory === "commercial-sell"
                  ? "border-[#503691] bg-purple-50"
                  : "border-gray-200 bg-white hover:border-purple-300 hover:shadow-md"
                  }`}
              >
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br rounded-full flex items-center justify-center">
                    <img src="./image/4.png" alt="Property Icon" />
                  </div>
                  <div className="text-sm font-medium text-gray-600">
                    Commercial Sell
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    (
                    {activeTab === "properties"
                      ? properties.filter(p => p?.type === "Commercial Sell").length
                      : requirements.filter(r =>
                        r?.type === "Commercial Sell" || r?.type === "commercial-sell"
                      ).length
                    }
                    )
                  </div>
                </div>
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        {activeTab === "properties" ? (
          <div>
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">
                My Properties ({filteredProperties.length})
              </h2>
            </div>

            {filteredProperties.length > 0 ? (
              <div className="space-y-6">
                {/* Your property cards will be displayed here when you uncomment the PropertyCard component */}
              </div>
            ) : (
              <div className="text-center py-12">
                <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No properties found
                </h3>
                <p className="text-gray-600 mb-4">
                  {properties.length === 0
                    ? "Start by adding your first property listing"
                    : "No properties match the selected category filter"}
                </p>
                <button
                  onClick={() => window.location.href = '/property-listed'}
                  className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  {properties.length === 0
                    ? "Add Property"
                    : "Show All Properties"}
                </button>
              </div>
            )}
          </div>
        ) : activeTab === "requirements" ? (
          <div>
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">
                My Requirements ({filteredRequirements.length})
              </h2>
            </div>

            {filteredRequirements.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Your requirement cards will be displayed here when you implement RequirementCard component */}
              </div>
            ) : (
              <div className="text-center py-12">
                <Home className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No requirements found
                </h3>
                <p className="text-gray-600 mb-4">
                  {requirements.length === 0
                    ? "Start by adding your property requirements"
                    : "No requirements match the selected category filter"}
                </p>
                <button
                  onClick={() => window.location.href = '/property-listed'}
                  className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  {requirements.length === 0
                    ? "Add Requirement"
                    : "Show All Requirements"}
                </button>
              </div>
            )}
          </div>
        ) : (
          // Builder Tab Content
          <div>
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">
                My Builder Properties ({filteredBuilderProperties.length})
              </h2>
            </div>

            {filteredBuilderProperties.length > 0 ? (
              <div className="space-y-6">
                {filteredBuilderProperties.map((property) => (
                  <div key={property._id || property.id} className="w-full">
                    {/* <PropertyCard property={property} /> */}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No builder properties found
                </h3>
                <p className="text-gray-600 mb-4">
                  {builderProperties.length === 0
                    ? "Start by adding your first builder property"
                    : "No builder properties match the selected category filter"}
                </p>
                <button
                  onClick={() => window.location.href = '/property-listed'}
                  className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  {builderProperties.length === 0
                    ? "Add Builder Property"
                    : "Show All Builder Properties"}
                </button>
              </div>
            )}
          </div>
        )}

      </div>


      {/* Property Cards Display */}
      <div className="min-h-screen bg-gray-50 px-0 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
        <div className="w-full">
          <div className="space-y-4 sm:space-y-6 lg:space-y-8">
            {activeTab === "properties" ? (
              <>
                {getCurrentItems().map((property) => (
                  <div
                    key={property._id || property.id}
                    className="w-full px-0 sm:px-2"
                  >
                    <PropertyCard property={property} />
                  </div>
                ))}

                {getCurrentItems().length === 0 && filteredProperties.length > 0 && (
                  <div className="text-center py-12">
                    <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No properties found on this page
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Try going to the first page or adjusting your filters
                    </p>
                    <button
                      onClick={() => setCurrentPage(1)}
                      className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Go to First Page
                    </button>
                  </div>
                )}

                {filteredProperties.length === 0 && properties.length > 0 && (
                  <div className="text-center py-12">
                    <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No properties found
                    </h3>
                    <p className="text-gray-600 mb-4">
                      No properties match the selected category filter
                    </p>
                    <button
                      onClick={() => setSelectedCategory("all")}
                      className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Show All Properties
                    </button>
                  </div>
                )}
              </>
            ) : activeTab === "requirements" ? (
              <>
                {getCurrentItems().map((requirement) => (
                  <div
                    key={requirement._id || requirement.id}
                    className="w-full px-0 sm:px-2"
                  >
                    <PropertyCard requirement={requirement} />
                  </div>
                ))}

                {getCurrentItems().length === 0 && filteredRequirements.length > 0 && (
                  <div className="text-center py-12">
                    <Home className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No requirements found on this page
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Try going to the first page or adjusting your filters
                    </p>
                    <button
                      onClick={() => setCurrentPage(1)}
                      className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Go to First Page
                    </button>
                  </div>
                )}

                {filteredRequirements.length === 0 && requirements.length > 0 && (
                  <div className="text-center py-12">
                    <Home className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No requirements found
                    </h3>
                    <p className="text-gray-600 mb-4">
                      No requirements match the selected category filter
                    </p>
                    <button
                      onClick={() => setSelectedCategory("all")}
                      className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Show All Requirements
                    </button>
                  </div>
                )}
              </>
            ) : (
              // Builder Properties
              <>
                {getCurrentItems().map((property) => (
                  <div
                    key={property._id || property.id}
                    className="w-full px-0 sm:px-2"
                  >
                    <PropertyCard property={property} />
                  </div>
                ))}

                {getCurrentItems().length === 0 && filteredBuilderProperties.length > 0 && (
                  <div className="text-center py-12">
                    <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No builder properties found on this page
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Try going to the first page or adjusting your filters
                    </p>
                    <button
                      onClick={() => setCurrentPage(1)}
                      className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Go to First Page
                    </button>
                  </div>
                )}

                {filteredBuilderProperties.length === 0 && builderProperties.length > 0 && (
                  <div className="text-center py-12">
                    <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No builder properties found
                    </h3>
                    <p className="text-gray-600 mb-4">
                      No builder properties match the selected category filter
                    </p>
                    <button
                      onClick={() => setSelectedBuilderCategory("all")}
                      className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Show All Builder Properties
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Pagination Component - Add this after the cards */}
          <Pagination />
        </div>
      </div>
    </div>
  );
};

// Individual Property Card Component
const PropertyCard = ({ property, requirement }) => {
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showDescMore, setShowDescMore] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(
    property?.propertyCurrentStatus ||
    requirement?.requirementStatus ||
    (requirement ? "Active" : "available")
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [newStatus, setNewStatus] = useState(currentStatus);
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const mediaInputRef = useRef(null);
  const [imagesConfirmed, setImagesConfirmed] = useState(false);
  const [selectedMediaKeys, setSelectedMediaKeys] = useState(new Set());
  const [showMediaOverlay, setShowMediaOverlay] = useState(false);
  const [imageLimitExceeded, setImageLimitExceeded] = useState(false);
  const [editData, setEditData] = useState({
    rtype: requirement?.type || "",
    rtitle: requirement?.title || "",
    subType: requirement?.subType || "",
    listedDate: requirement?.listedDate || "",
    rpriceRange: requirement?.priceRange || { min: "", max: "" },
    furnishingPreferences: requirement?.furnishingPreferences || [],
    areas: requirement?.areas || [],
    rbhk: requirement?.bhk || "",
    sizeRange: {
      sqFt: {
        min: requirement?.sizeRange?.sqFt?.min || "",
        max: requirement?.sizeRange?.sqFt?.max || "",
      },
    },
    brokerName: requirement?.brokerName || "",
    contactNumber: requirement?.contactNumber || "",
    rdescription: requirement?.description || "",

    // For broker properties  
    type: property?.type || "",
    title: property?.title || "",
    price: property?.price || "",
    unitType: property?.unitType || "",
    area: property?.area || "",
    bhk: property?.bhk || "",
    furnishedType: property?.furnishedType || "",
    address: property?.address || "",
    sqFt: property?.sqFt || "",
    description: property?.description || "",
    name: property?.name || "",
    number: property?.number || "",
    media: property?.media || [],
    // builder properties
    priceRange: property?.priceRange || { min: "", max: "" },
  });


  const navigate = useNavigate();

  //  const [activeTab, setActiveTab] = useState('properties');

  const isRequirement = !!requirement;
  const data = isRequirement ? requirement : property;

  const residentialSubTypes = [
    "Apartment",
    "Bungalow",
    "Tenement",
    "Penthouse",
    "Weekend Home",
    "PG",
    "Rowhouse",
    "Residential Plot",
    "Pre Leased Spaces",
  ];
  const commercialSubtypes = [
    "Basement",
    "Commercial Plot",
    "Shed",
    "Co Working Space",
    "Factory",
    "Shop",
    "Commercial Building",
    "Godown",
    "Showroom",
    "Restaurant",
    "Industrial Land",
    "Commercial Flat",
    "Office",
    "Ware House",
    "Pre Leased Spaces",
    "Commericial Bunglow",
    "Land",
    "Agriculture Land",
    "PG House",
    "Hotel",
    "Gym",
    "Spa",
    "Hospital",
    "Salon",
    "Petrol Pump",
    "Cafe",
    "PreSchool",
    "Industrial Plot",
    "Industrial Shed",

  ];

  const furnishingOptions = [
    "Fully Furnished",
    "Furnished",
    "Semi-Furnished",
    "Kitchen-Fix",
    "Unfurnished",
  ];

  const bhkOptions = [
    "Studio Apartment",
    "1 BHK",
    "2 BHK",
    "3 BHK",
    "4 BHK",
    "5 BHK",
    "6 BHK",
    "6+ BHK",
  ];

  // Check if the screen size is mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleContactClick = () => {
    // Set the contact information to display
    setContactInfo({
      name: property?.name || "NA",
      number: property?.number || "NA",
    });
    setShowContact(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "NA";
    const date = new Date(dateString);
    const day = date.getUTCDate();
    const month = date.getUTCMonth() + 1;
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
  };

  const truncateText = (text, wordLimit) => {
    if (!text) return "NA";
    const words = text.split(" ");
    if (words.length > wordLimit) {
      return words.slice(0, wordLimit).join(" ") + "...";
    }
    return text;
  };
  const truncateDescription = (text, limit = 100) => {
    if (!text) return "NA";
    if (text.length <= limit) return text;
    return text.slice(0, limit) + "...";
  };

  const totalWord = 20;
  const isDescriptionTooLong =
    property?.description?.split(" ").length > totalWord;
  const truncatedDescription = truncateText(property?.description, totalWord);



  const copyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        setToast({ message: "Content copied to clipboard!", type: "success" });
      }).catch(() => {
        const temp = document.createElement("textarea");
        temp.value = text;
        document.body.appendChild(temp);
        temp.select();
        document.execCommand("copy");
        document.body.removeChild(temp);
        setToast({ message: "Content copied to clipboard!", type: "success" });
      });
    } else {
      const temp = document.createElement("textarea");
      temp.value = text;
      document.body.appendChild(temp);
      temp.select();
      document.execCommand("copy");
      document.body.removeChild(temp);
      setToast({ message: "Content copied to clipboard!", type: "success" });
    }
  };

  const buildShareText = () => {
    const isReq = !!requirement;
    const t = isReq ? requirement?.type : property?.type;
    let header = "Property Available";
    if (t === "Residential Rent" || t === "Residential Sell") {
      const bhkVal = isReq ? requirement?.bhk : property?.bhk;
      const unitVal = isReq ? requirement?.unitType : property?.unitType;
      header = `${bhkVal || ""}${bhkVal ? " " : ""}${unitVal || ""} Available for ${t === "Residential Rent" ? "Rent" : "Sale"}`;
    } else if (t === "Commercial Rent" || t === "Commercial Sell") {
      const unitVal = isReq ? requirement?.unitType : property?.unitType;
      header = `${unitVal || ""} Available for ${t === "Commercial Rent" ? "Rent" : "Sale"}`;
    }

    const premise = isReq ? (property?.title || "NA") : (property?.title || "NA");
    const addrArea = isReq ? (Array.isArray(requirement?.areas) ? requirement.areas.join(", ") : requirement?.areas) : property?.area;
    const addrCity = property?.city || "NA";

    const sqFtText = isReq
      ? (requirement?.sizeRange?.sqFt ? `⦁ Square Ft: ${requirement.sizeRange.sqFt.min || 0}-${requirement.sizeRange.sqFt.max || 0} SqFt` : "⦁ Square Ft: N/A")
      : `⦁ Square Ft: ${property?.sqFt || "NA"} SqFt`;

    const furnished = isReq
      ? (requirement?.furnishingPreferences && requirement.furnishingPreferences.length > 0 ? requirement.furnishingPreferences.join(", ") : (requirement?.furnishedType || "NA"))
      : (property?.furnishedType || "NA");

    let priceLine = "";
    if (isReq) {
      priceLine = requirement?.priceRange ? `⦁ Price: ${formatPriceRange(requirement.priceRange)}` : "⦁ Price: N/A";
    } else {
      if (property?.listedBy === "builders" && property?.priceRange) {
        priceLine = `⦁ Price: ${getBuilderPrice(property)}`;
      } else if (t && t.includes("Rent")) {
        priceLine = `⦁ Rent: ${formatPrice(property?.price)}`;
      } else if (t && t.includes("Sell")) {
        priceLine = `⦁ Sell: ${formatPrice(property?.price)}`;
      } else {
        priceLine = `⦁ Price: ${formatPrice(property?.price)}`;
      }
    }

    const pid = isReq ? requirement?.requirementId : property?.propertyId;

    const shareName = isReq ? (requirement?.brokerName || "") : (property?.name || "");
    const shareNumber = isReq ? (requirement?.contactNumber || "") : (property?.number || "");

    const text = `**${header}**

⦁ Premise: ${premise}

⦁ Address: ${addrArea || "NA"}, ${addrCity}

${sqFtText}

⦁ Furnished Type: ${furnished}

${priceLine}

⦁ PID: ${pid}

**Name: ${shareName || "NA"}**
**Number: ${shareNumber || "NA"}**`.trim();
    return text;
  };

  const handleShareClick = async () => {
    const shareText = buildShareText();
    if (navigator.share) {
      try {
        await navigator.share({ title: "Property Details", text: shareText });
      } catch (e) { }
    }
    copyToClipboard(shareText);
    const isMobileDevice = /Mobi|Android/i.test(navigator.userAgent);
    if (!navigator.share && isMobileDevice) {
      const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;
      window.open(whatsappUrl, "_blank");
    }
  };

  // Handle status change from dropdown
  const handleStatusChange = (status) => {
    setNewStatus(status);
    setShowConfirmModal(true);
  };

  // Update property status
  const updatePropertyStatus = async () => {
    const token = Cookies.get("token");
    try {
      const result = await apiClient(
        "PUT",
        `/cjidnvij/ceksfbuebijn/user/brokerproperties/${property._id}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!result.success) {
        throw new Error(result.error || "Failed to update status");
      }

      setCurrentStatus(newStatus);
      setShowConfirmModal(false);
      setToast({ message: "Status updated successfully", type: "success" });
    } catch (error) {
      console.error("Error updating status:", error);
      setToast({ message: "Failed to update status", type: "error" });
    }
  };

  // Update requirement status
  const updateRequirementStatus = async () => {
    const token = Cookies.get("token");
    try {
      const result = await apiClient(
        "PUT",
        `/cjidnvij/ceksfbuebijn/user/brokerrequirements/${requirement._id}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!result.success) {
        throw new Error(result.error || "Failed to update requirement status");
      }

      setCurrentStatus(newStatus);
      setShowConfirmModal(false);
      setToast({ message: "Status updated successfully", type: "success" });
    } catch (error) {
      console.error("Error updating requirement status:", error);
      setToast({ message: "Failed to update status", type: "error" });
    }
  };

  // Handle confirm action
  const handleConfirm = () => {
    if (isRequirement) {
      updateRequirementStatus();
    } else {
      updatePropertyStatus();
    }
  };

  // Handle cancel action
  const handleCancel = () => {
    setShowConfirmModal(false);
    setNewStatus(currentStatus);
  };

  // Close toast
  const closeToast = () => {
    setToast(null);
  };

  // Handle property deletion
  const handleDeleteProperty = async () => {
    const token = Cookies.get("token");
    try {
      const result = await apiClient(
        "DELETE",
        `/cjidnvij/ceksfbuebijn/user/brokerproperties/${property._id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!result.success) {
        throw new Error(result.error || "Failed to delete property");
      }

      setShowDeleteModal(false);
      setToast({ message: "Property deleted successfully", type: "success" });
      // Optionally refresh the property list or remove from UI
      window.location.reload();
    } catch (error) {
      console.error("Error deleting property:", error);
      setToast({ message: "Failed to delete property", type: "error" });
    }
  };

  const handleMediaButtonClick = () => {
    if (isEditing && mediaInputRef.current) {
      mediaInputRef.current.click();
    }
  };

  const handleMediaInputChange = (e) => {
    const originalFiles = Array.from(e.target.files || []);
    const files = originalFiles.filter((f) =>
      f.type.startsWith("image/") || f.type.startsWith("video/")
    );

    if (files.length < originalFiles.length) {
      setToast({
        message: "Only image and video files are allowed.",
        type: "error"
      });
    }

    const newItems = files.map((file) => ({
      file,
      type: file.type.startsWith("video/") ? "video" : "image",
      name: file.name,
      preview: URL.createObjectURL(file),
    }));

    setEditData((prev) => ({
      ...prev,
      media: [...(prev.media || []), ...newItems],
    }));

    const total = (editData.media?.length || 0) + newItems.length;
    if (total > 10) {
      setImageLimitExceeded(true);
      setToast({ message: "Only 10 media files allowed", type: "error" });
    } else {
      setImageLimitExceeded(false);
    }
  };

  const handleSaveProperty = async () => {
    const token = Cookies.get("token");
    const hasNewMedia =
      Array.isArray(editData.media) &&
      editData.media.some((m) => m && m.file);

    let result;
    if (hasNewMedia) {
      const uploadData = new FormData();
      uploadData.append("type", editData.type || "");
      uploadData.append("title", editData.title || "");
      uploadData.append("unitType", editData.unitType || "");
      uploadData.append("area", editData.area || "");
      uploadData.append("bhk", Array.isArray(editData.bhk) ? editData.bhk.join("|") : (editData.bhk || ""));
      uploadData.append("furnishedType", editData.furnishedType || "");
      uploadData.append("address", editData.address || "");
      uploadData.append("sqFt", editData.sqFt || "");
      uploadData.append("description", editData.description || "");
      uploadData.append("name", editData.name || "");
      uploadData.append("number", editData.number || "");
      uploadData.append("listedDate", property?.listedDate || "");
      if (property?.listedBy === "builders") {
        uploadData.append("priceRange[min]", editData?.priceRange?.min ?? "");
        uploadData.append("priceRange[max]", editData?.priceRange?.max ?? "");
      } else {
        uploadData.append("price", editData.price ?? "");
        uploadData.append("priceRange[min]", editData.price ?? "");
        uploadData.append("priceRange[max]", editData.price ?? "");
      }
      const mediaObjects = [];
      editData.media
        .filter((m) => m && m.file)
        .forEach((mediaItem, index) => {
          uploadData.append("media", mediaItem.file);
          mediaObjects.push({
            type: mediaItem.type || (mediaItem.file.type.startsWith("video/") ? "video" : "image"),
            name: mediaItem.name,
            index,
          });
        });
      uploadData.append("mediaMetadata", JSON.stringify(mediaObjects));
      result = await apiClient(
        "PUT",
        `/cjidnvij/ceksfbuebijn/user/brokerproperties/${property._id}`,
        uploadData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } else {
      const payload = {
        ...editData,
        listedDate: property?.listedDate,
      };
      result = await apiClient(
        "PUT",
        `/cjidnvij/ceksfbuebijn/user/brokerproperties/${property._id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    }

    if (result?.success) {
      setToast({ message: "Property updated!", type: "success" });
      window.location.reload();
    } else {
      setToast({ message: "Failed to update", type: "error" });
    }
  };
  const getMediaUrl = (m) => m?.preview || m?.url || "";
  useEffect(() => {
    const keys = new Set(
      (editData.media || [])
        .map((m) => getMediaUrl(m))
        .filter((u) => typeof u === "string" && u.trim() !== "")
    );
    setSelectedMediaKeys(keys);
    setImagesConfirmed(false);
  }, [editData.media]);
  useEffect(() => {
    const count = selectedMediaKeys.size;
    setImageLimitExceeded(count > 10);
  }, [selectedMediaKeys]);
  const toggleMediaSelection = (url) => {
    setSelectedMediaKeys((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };
  const handleRemoveMedia = (url) => {
    setEditData((prev) => ({
      ...prev,
      media: (prev.media || []).filter((m) => getMediaUrl(m) !== url),
    }));
    setSelectedMediaKeys((prev) => {
      const next = new Set(prev);
      next.delete(url);
      return next;
    });
  };
  const handleConfirmImages = () => {
    if (imageLimitExceeded) {
      setToast({ message: "Only 10 images allowed", type: "error" });
      return;
    }
    const filtered = (editData.media || []).filter((m) =>
      selectedMediaKeys.has(getMediaUrl(m))
    );
    setEditData((prev) => ({ ...prev, media: filtered }));
    setImagesConfirmed(true);
    setToast({ message: "Images confirmed", type: "success" });
    setShowMediaOverlay(false);
  };

  const handleSaveRequirement = async () => {
    const token = Cookies.get("token");
    const payload = {
      type: editData.rtype,                // ✅ REQUIRED BY BACKEND
      title: editData.rtitle,
      subType: editData.subType,
      listedDate: editData.listedDate,     // ✅ Prevents NA date
      priceRange: editData.rpriceRange,
      furnishingPreferences: editData.furnishingPreferences,
      areas: editData.areas,
      bhk: editData.rbhk,
      sizeRange: editData.sizeRange,
      brokerName: editData.brokerName,
      contactNumber: editData.contactNumber,
      description: editData.rdescription, // ✅ Must be named 'description'
    };

    try {
      const result = await apiClient(
        "PUT",
        `/cjidnvij/ceksfbuebijn/user/brokerrequirements/${requirement._id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!result?.success) throw new Error(result.error || "Update failed");

      setIsEditing(false);
      setToast({ message: "Requirement updated!", type: "success" });

      window.location.reload();   // OR manually update requirement state
    } catch (err) {
      setToast({ message: err.message, type: "error" });
    }
  };


  // Handle requirement deletion
  const handleDeleteRequirement = async () => {
    const token = Cookies.get("token");
    try {
      const result = await apiClient(
        "DELETE",
        `/cjidnvij/ceksfbuebijn/user/brokerrequirements/${requirement._id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!result.success) {
        throw new Error(result.error || "Failed to delete requirement");
      }

      setShowDeleteModal(false);
      setToast({
        message: "Requirement deleted successfully",
        type: "success",
      });
      window.location.reload();
    } catch (error) {
      console.error("Error deleting requirement:", error);
      setToast({ message: "Failed to delete requirement", type: "error" });
    }
  };

  // Handle match properties - the match page loads matches for every
  // requirement on its own, so this only deep links into it with a filter.
  const handleMatchProperty = (requirementId) => {
    if (!requirementId) {
      setToast({ message: "Requirement ID not found", type: "error" });
      return;
    }

    navigate(`/match-property?requirement=${requirementId}`);
  };

  // Render Requirement card when requirement is passed
  if (isRequirement) {
    const priceRangeText = requirement?.priceRange
      ? formatPriceRange(requirement.priceRange)
      : "NA";


    const sqftRangeText = requirement?.sizeRange?.sqFt
      ? requirement.sizeRange.sqFt.max === 0 || requirement.sizeRange.sqFt.max === requirement.sizeRange.sqFt.min
        ? `${requirement.sizeRange.sqFt.min} sqft`
        : `${requirement.sizeRange.sqFt.min ?? "NA"} - ${requirement.sizeRange.sqFt.max ?? "NA"} sqft`
      : "NA";

    const sqydRangeText = requirement?.sizeRange?.sqYd &&
      (requirement.sizeRange.sqYd.min > 0 || requirement.sizeRange.sqYd.max > 0)
      ? requirement.sizeRange.sqYd.max === 0 || requirement.sizeRange.sqYd.max === requirement.sizeRange.sqYd.min
        ? `${requirement.sizeRange.sqYd.min} sqyd`
        : `${requirement.sizeRange.sqYd.min ?? "NA"} - ${requirement.sizeRange.sqYd.max ?? "NA"} sqyd`
      : null;
    const areasText =
      Array.isArray(requirement?.areas) && requirement.areas.length > 0
        ? requirement.areas.join(", ")
        : "NA";
    const bhkText =
      Array.isArray(requirement?.bhk) && requirement.bhk.length > 0
        ? requirement.bhk.join(", ")
        : "NA";
    const furnishingMap = {
      Furnished: "Furnished",
      "Semi-Furnished": "Semi-furnished",
      Unfurnished: "Unfurnished",
    };
    const furnishingText =
      Array.isArray(requirement?.furnishingPreferences) &&
        requirement.furnishingPreferences.length > 0
        ? requirement.furnishingPreferences
          .map((f) => furnishingMap[f] || f)
          .join(", ")
        : "NA";
    const reqDesc = requirement?.description || "";

    return (
      <div className="bg-white rounded-lg shadow-md w-full">
        {/* Toast Notification */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={closeToast}
          />
        )}

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={showConfirmModal}
          onClose={handleCancel}
          onConfirm={handleConfirm}
          status={newStatus}
        />

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteRequirement}
        />

        {/* Header - Requirement */}
        {/* Mobile View */}
        <div className="flex lg:hidden flex-col gap-3 w-full">
          {/* First Row: Requirement Type and Price Range */}
          <div className="flex flex-row justify-between gap-2 w-full">
            {/* <p className="text-[#503691] bg-[#EFE9FF] font-bold px-3 py-2 rounded-full border border-blue-700 text-sm truncate flex-1 text-center min-w-0">
     {requirement?.type || "NA"}
    </p> */}
            {!isEditing ? (
              <>
                <p className="text-[#503691] bg-[#EFE9FF] font-bold px-3 py-2 rounded-full border border-blue-700 text-sm truncate flex-1 text-center min-w-0">
                  {(requirement?.type || "").includes("Rent") ? "Rent" : "Sell"}: {priceRangeText}
                </p>
              </>
            ) : (
              <div className="flex flex-col gap-2 w-full">
                <div className="flex items-center gap-2 bg-white border border-blue-700 px-3 py-2 rounded-full text-sm flex-1 min-w-0">
                  {/* Rent / Sell */}
                  <span className="text-[#503691] font-bold">
                    {(editData?.rtype || "").includes("Rent") ? "Rent" : "Sell"}:
                  </span>

                {/* Min Price */}
                <input
                  type="number"
                  placeholder="Min"
                  className="border rounded px-2 py-1 w-20 text-center"
                  value={editData?.rpriceRange?.min ?? ""}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      rpriceRange: {
                        ...editData.rpriceRange,
                        min: e.target.value,
                      },
                    })
                  }
                />

                <span>-</span>

                {/* Max Price */}
                <input
                  type="number"
                  placeholder="Max"
                  className="border rounded px-2 py-1 w-20 text-center"
                  value={editData?.rpriceRange?.max ?? ""}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      rpriceRange: {
                        ...editData.rpriceRange,
                        max: e.target.value,
                      },
                    })
                  }
                />
              </div>
            </div>
          )}

          </div>

          {/* Second Row: Verification, Date, ID and Status */}
          <div className="flex flex-row justify-between items-center gap-2 w-full">
            {/* Left Side: Verification, Date, ID */}
            <div className="flex flex-row gap-2 items-center flex-1 min-w-0">
              {/* Verification Dot */}
              <div className="relative group flex-shrink-0">
                <div
                  className={`w-3 h-3 rounded-full ${requirement?.verifyStatus === "unverified"
                    ? "bg-yellow-400"
                    : "bg-green-500"
                    }`}
                ></div>
                <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-max px-3 py-1 rounded bg-gray-800 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 whitespace-nowrap shadow-md">
                  {requirement?.verifyStatus === "unverified"
                    ? "This requirement is unverified"
                    : "This requirement is verified"}
                </div>
              </div>

              {/* Date */}
              <div className="rounded-full bg-gray-100 text-sm text-red-500 font-bold px-3 py-1 whitespace-nowrap flex-shrink-0">
                {formatDate(requirement?.listedDate)}
              </div>

              {/* ID */}
              <div className="rounded-full bg-gray-100 text-sm text-blue-600 font-bold px-3 py-1 whitespace-nowrap flex-shrink-0">
                ID: {requirement?.requirementId || "NA"}
              </div>
            </div>

            {/* Right Side: Status Dropdown */}
            <div className="flex-shrink-0">
              <StatusDropdown
                status={currentStatus}
                onChange={handleStatusChange}
                propertyType={requirement?.type}
                options={[
                  { value: "Active", label: "Active", color: "bg-pink-500" },
                  { value: "Fulfilled", label: "Fulfilled", color: "bg-blue-500" },
                  { value: "Cancelled", label: "Cancelled", color: "bg-red-500" },
                ]}
              />
            </div>
          </div>
        </div>

        {/* Desktop View */}
        <div className="hidden lg:grid grid-cols-3 items-center gap-3 pt-4 px-4 w-full">
          {/* Left side - Status, Date, ID */}
          <div className="flex flex-wrap items-center gap-2 min-w-0 justify-start">
            <div className="relative group">
              <div
                className={`w-3 h-3 rounded-full ${requirement?.verifyStatus === "unverified"
                  ? "bg-yellow-400"
                  : "bg-green-500"
                  }`}
              ></div>
              <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-max px-3 py-1 rounded bg-gray-800 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 whitespace-nowrap shadow-md">
                {requirement?.verifyStatus === "unverified"
                  ? "This requirement is unverified"
                  : "This requirement is verified"}
              </div>
            </div>

            <div className="rounded-full text-red-500 font-bold px-3 py-1 border border-red-500 whitespace-nowrap text-sm">
              {formatDate(requirement?.listedDate)}
            </div>
            <div className="rounded-full text-blue-600 font-bold px-3 py-1 border border-blue-600 whitespace-nowrap text-sm">
              ID: {requirement?.requirementId || "NA"}
            </div>
          </div>

          {/* Center - Rent/Sell info */}
          <div className="flex justify-center">
            {!isEditing ? (
              <>
                <p className="text-[#503691] bg-[#EFE9FF] font-bold px-3 py-2 rounded-full border border-indigo-600 whitespace-nowrap text-sm">
                  {(requirement?.type || "").includes("Rent") ? "Rent" : "Sell"}: {priceRangeText}
                </p>
              </>
            ) : (
              <div className="flex items-center gap-2 bg-white border border-indigo-600 px-3 py-2 rounded-full text-sm">
                <p className="text-[#503691] font-bold">
                  {(editData?.rtype || "").includes("Rent") ? "Rent" : "Sell"}:
                </p>

                {/* MIN Price */}
                <input
                  type="number"
                  placeholder="Min"
                  className="border rounded px-2 py-1 w-20 text-right"
                  value={editData?.rpriceRange?.min ?? ""}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      rpriceRange: {
                        ...editData.rpriceRange,
                        min: e.target.value,
                      },
                    })
                  }
                />

                <span>-</span>

                {/* MAX Price */}
                <input
                  type="number"
                  placeholder="Max"
                  className="border rounded px-2 py-1 w-20 text-right"
                  value={editData?.rpriceRange?.max ?? ""}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      rpriceRange: {
                        ...editData.rpriceRange,
                        max: e.target.value,
                      },
                    })
                  }
                />
              </div>
            )}
          </div>


          {/* Right side - Action Buttons */}
          <div className="flex flex-wrap items-center gap-2 min-w-0 justify-end">
            {/* Status Dropdown */}
            <StatusDropdown
              status={currentStatus}
              onChange={handleStatusChange}
              propertyType={requirement?.type}
              options={[
                { value: "Active", label: "Active", color: "bg-green-500" },
                { value: "Fulfilled", label: "Fulfilled", color: "bg-blue-500" },
                { value: "Cancelled", label: "Cancelled", color: "bg-red-500" },
              ]}
            />

            {/* Share button */}
            <div className="flex items-center gap-2 bg-[#503691] hover:bg-white text-white hover:text-blue-800 border border-blue-800 px-3 h-10 rounded-lg cursor-pointer" onClick={handleShareClick}>
              <FaShareAlt className="h-4 w-4" />
            </div>

            {/* Edit button */}
            <div
              className="flex items-center gap-2 border border-[#503691] px-3 py-2.5 rounded-lg cursor-pointer bg-white text-[#503691] hover:bg-[#503691] hover:text-white"
              onClick={() => setIsEditing(!isEditing)}
            >
              <MdNoteAlt className="h-4 w-4" />
              {isEditing ? "Close" : "Edit"}
            </div>

            {isEditing && (
              <button
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                onClick={handleSaveRequirement}
              >
                Save
              </button>
            )}



            {/* Delete button */}
            <div
              className="flex items-center gap-2 border border-red-600 px-3 py-2.5 rounded-lg cursor-pointer bg-white text-red-600 hover:bg-red-600 hover:text-white"
              onClick={() => setShowDeleteModal(true)}
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </div>

            {/* Match Properties button */}
            {/* <div
              className="flex items-center gap-2 border border-green-600 px-3 py-2.5 rounded-lg cursor-pointer bg-white text-green-600 hover:bg-green-600 hover:text-white"
              onClick={() => handleMatchProperty(requirement?._id)}
            >
              <FaRegBookmark className="h-4 w-4" />
              Match Properties
            </div> */}
          </div>
        </div>

        {/* Main content area - Requirement details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 text-sm lg:text-md gap-4 p-2 lg:p-4 divide-x lg:divide-solid divide-none lg:divide-black">
          <div className="grid grid-cols-1 gap-2">
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-row items-center">
                <Building className="w-5 h-5 text-indigo-600 mr-2" />
                <p>Premise</p>
              </div>

              {!isEditing ? (
                <p className="text-end font-medium">{requirement?.title || "NA"}</p>
              ) : (
                <input
                  type="text"
                  placeholder="Enter Premise Name"
                  className="border rounded px-2 py-1 text-end focus:outline-none"
                  value={editData.rtitle}
                  onChange={(e) =>
                    setEditData({ ...editData, rtitle: e.target.value })
                  }
                />
              )}
            </div>
            <hr className="block" />
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-row items-center">
                <Building className="w-5 h-5 text-yellow-500 mr-2" />
                <p>Type</p>
              </div>

              {!isEditing ? (
                <p className="text-end">{requirement?.type || "NA"}</p>
              ) : (
                <select
                  className="border rounded px-2 py-1 text-end"
                  value={editData.rtype}
                  onChange={(e) =>
                    setEditData({ ...editData, rtype: e.target.value })
                  }
                >
                  <option value="">Select Type</option>
                  <option value="Residential Rent">Residential Rent</option>
                  <option value="Residential Sell">Residential Sell</option>
                  <option value="Commercial Rent">Commercial Rent</option>
                  <option value="Commercial Sell">Commercial Sell</option>
                </select>
              )}
            </div>
            <hr className="block" />


            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-row items-center">
                <GoHome className="text-[#247d00] mr-2 h-6 w-6" />
                <p>Subtype</p>
              </div>

              {!isEditing ? (
                <p className="text-end">{requirement?.subType || "NA"}</p>
              ) : (
                <>
                  {(editData.type === "Residential Rent" ||
                    editData.type === "Residential Sell") && (
                      <select
                        className="border rounded px-2 py-1 text-end"
                        value={editData.subType}
                        onChange={(e) =>
                          setEditData({ ...editData, subType: e.target.value })
                        }
                      >
                        <option value="">Select Subtype</option>
                        {residentialSubTypes.map((item) => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </select>
                    )}

                  {(editData.type === "Commercial Rent" ||
                    editData.type === "Commercial Sell") && (
                      <select
                        className="border rounded px-2 py-1 text-end"
                        value={editData.subType}
                        onChange={(e) =>
                          setEditData({ ...editData, subType: e.target.value })
                        }
                      >
                        <option value="">Select Subtype</option>
                        {commercialSubtypes.map((item) => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </select>
                    )}

                  {/* Fallback if no match */}
                  {![
                    "Residential Rent",
                    "Residential Sell",
                    "Commercial Rent",
                    "Commercial Sell",
                  ].includes(editData.type) && (
                      <input
                        className="border rounded px-2 py-1 text-end"
                        value={editData.subType}
                        onChange={(e) =>
                          setEditData({ ...editData, subType: e.target.value })
                        }
                      />
                    )}
                </>
              )}
            </div>

            <hr className="block" />



            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-row items-center">
                <MapPin className="w-5 h-5 text-blue-500 mr-2" />
                <p>Areas</p>
              </div>
              {!isEditing ? (
                <p className="text-end">{areasText}</p>
              ) : (
                <input
                  className="border rounded px-2 py-1 w-full text-right"
                  value={editData.areas.join(", ")}
                  onChange={(e) =>
                    setEditData({ ...editData, areas: e.target.value.split(",") })
                  }
                />
              )}

            </div>
            <hr className="block" />

            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-row items-center">
                <FaBed className="text-[#f1d35c] mr-2 h-6 w-6" />
                <p>BHK</p>
              </div>
              {!isEditing ? (
                <p className="text-end">
                  {["Commercial Rent", "Commercial Sell"].includes(editData.rtype)
                    ? "NA"
                    : editData.rbhk || "NA"}
                </p>
              ) : (
                <>
                  {["Residential Rent", "Residential Sell"].includes(editData.rtype) ? (
                    <select
                      className="border rounded px-2 py-1 text-end"
                      value={editData.rbhk}
                      onChange={(e) =>
                        setEditData({ ...editData, rbhk: e.target.value })
                      }
                    >
                      <option value="">Select BHK</option>
                      {bhkOptions.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-end text-gray-400">NA</p>
                  )}
                </>
              )}



            </div>
            <hr className="block" />

            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-row items-center">
                <TbLamp className="text-[#5f8de9] mr-2 h-6 w-6" />
                <p>Furnishing</p>
              </div>
              {!isEditing ? (
                <p className="text-end">{editData.furnishingPreferences || "NA"}</p>
              ) : (
                <select
                  className="border rounded px-2 py-1 text-end"
                  value={editData.furnishingPreferences}
                  onChange={(e) =>
                    setEditData({ ...editData, furnishingPreferences: e.target.value })
                  }
                >
                  <option value="">Select Furnishing</option>
                  {furnishingOptions.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              )}

            </div>
          </div>

          <div className="grid grid-cols-1 gap-2 pl-0 lg:pl-4">
            <hr className="lg:hidden block" />

            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-row items-center">
                <RxDimensions className="text-[#f1d35c] mr-2 h-6 w-6" />
                <p>Size Range</p>
              </div>
              <div className="text-end">
                {!isEditing ? (
                  <p>{sqftRangeText}</p>
                ) : (
                  <div className="flex items-center gap-2 justify-end">
                    {/* Min sqft */}
                    <input
                      type="number"
                      placeholder="Min"
                      className="border rounded px-2 py-1 w-20 text-right"
                      value={editData?.sizeRange?.sqFt?.min ?? ""}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          sizeRange: {
                            ...editData.sizeRange,
                            sqFt: {
                              ...editData.sizeRange.sqFt,
                              min: e.target.value,
                            },
                          },
                        })
                      }
                    />
                    <span>-</span>

                    {/* Max sqft */}
                    <input
                      type="number"
                      placeholder="Max"
                      className="border rounded px-2 py-1 w-20 text-right"
                      value={editData?.sizeRange?.sqFt?.max ?? ""}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          sizeRange: {
                            ...editData.sizeRange,
                            sqFt: {
                              ...editData.sizeRange.sqFt,
                              max: e.target.value,
                            },
                          },
                        })
                      }
                    />
                    <span>sqft</span>
                  </div>
                )}

              </div>
            </div>
            <hr className="block" />

            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-row items-center">
                <IoDocumentTextOutline className="text-[#247d00] mr-2 h-6 w-6" />
                <p>Description</p>
              </div>

              <div className="flex flex-col items-end w-full">
                {!isEditing ? (
                  <>
                    <p className="text-end">
                      {showFullDescription ? reqDesc : truncateDescription(reqDesc)}
                    </p>

                    {reqDesc && reqDesc.length > 100 && (
                      <button
                        onClick={() => setShowFullDescription(!showFullDescription)}
                        className="text-blue-600 hover:underline text-sm mt-1"
                      >
                        {showFullDescription ? "Show Less" : "Show More"}
                      </button>
                    )}
                  </>
                ) : (
                  <textarea
                    className="border rounded px-2 py-1 w-full text-right"
                    rows={3}
                    value={editData.rdescription}
                    onChange={(e) =>
                      setEditData({ ...editData, rdescription: e.target.value })
                    }
                  />
                )}
              </div>

            </div>
            <hr className="block" />

            <div className="lg:flex hidden items-center justify-between gap-4">
              <div className="flex flex-row items-center">
                <MdOutlinePhone className="text-[#503691] mr-2 h-6 w-6" />
                <p>Contact</p>
              </div>

              {showContact && contactInfo ? (
                <div className="flex items-center gap-3">
                  <div className="bg-green-50 border border-green-200 rounded-full px-4 py-2">
                    {!isEditing ? (
                      <p className="text-green-700 font-medium">
                        {requirement?.brokerName} - {requirement?.contactNumber} 📍
                      </p>
                    ) : (
                      <div className="flex flex-col items-end gap-2 w-full">
                        {/* Broker Name */}
                        <input
                          type="text"
                          className="border rounded px-2 py-1 w-full text-right"
                          placeholder="Broker Name"
                          value={editData.brokerName}
                          onChange={(e) =>
                            setEditData({ ...editData, brokerName: e.target.value })
                          }
                        />

                        {/* Contact Number */}
                        <input
                          type="text"
                          className="border rounded px-2 py-1 w-full text-right"
                          placeholder="Contact Number"
                          value={editData.contactNumber}
                          onChange={(e) =>
                            setEditData({ ...editData, contactNumber: e.target.value })
                          }
                        />
                      </div>
                    )}
                  </div>
                  {/* Phone Link */}
                  <a
                    href={`tel:${typeof contactInfo.number === "string" &&
                      contactInfo.number.startsWith("+91")
                      ? contactInfo.number.replace("+91", "").trim()
                      : contactInfo.number || ""
                      }`}
                    className="text-[#503691] hover:text-blue-600 transition-colors"
                    title="Call"
                  >
                    <MdOutlinePhone className="h-6 w-6" />
                  </a>

                  {/* WhatsApp Link */}
                  <a
                    href={`https://wa.me/${typeof contactInfo.number === "string" &&
                      contactInfo.number.startsWith("+91")
                      ? contactInfo.number.replace("+91", "").trim()
                      : contactInfo.number || ""
                      }`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:scale-110 transition-transform"
                    title="WhatsApp"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 448 512"
                      className="h-6 w-6"
                      fill="#25D366"
                    >
                      <path d="M92.1 254.6c0 24.9 7 49.2 20.2 70.1l3.1 5-13.3 48.6L152 365.2l4.8 2.9c20.2 12 43.4 18.4 67.1 18.4h.1c72.6 0 133.3-59.1 133.3-131.8c0-35.2-15.2-68.3-40.1-93.2c-25-25-58-38.7-93.2-38.7c-72.7 0-131.8 59.1-131.9 131.8zM274.8 330c-12.6 1.9-22.4 .9-47.5-9.9c-36.8-15.9-61.8-51.5-66.9-58.7c-.4-.6-.7-.9-.8-1.1c-2-2.6-16.2-21.5-16.2-41c0-18.4 9-27.9 13.2-32.3c.3-.3 .5-.5 .7-.8c3.6-4 7.9-5 10.6-5c2.6 0 5.3 0 7.6 .1c.3 0 .5 0 .8 0c2.3 0 5.2 0 8.1 6.8c1.2 2.9 3 7.3 4.9 11.8c3.3 8 6.7 16.3 7.3 17.6c1 2 1.7 4.3 .3 6.9c-3.4 6.8-6.9 10.4-9.3 13c-3.1 3.2-4.5 4.7-2.3 8.6c15.3 26.3 30.6 35.4 53.9 47.1c4 2 6.3 1.7 8.6-1c2.3-2.6 9.9-11.6 12.5-15.5c2.6-4 5.3-3.3 8.9-2s23.1 10.9 27.1 12.9c.8 .4 1.5 .7 2.1 1c2.8 1.4 4.7 2.3 5.5 3.6c.9 1.9 .9 9.9-2.4 19.1c-3.3 9.3-19.1 17.7-26.7 18.8zM448 96c0-35.3-28.7-64-64-64H64C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V96zM148.1 393.9L64 416l22.5-82.2c-13.9-24-21.2-51.3-21.2-79.3C65.4 167.1 136.5 96 223.9 96c42.4 0 82.2 16.5 112.2 46.5c29.9 30 47.9 69.8 47.9 112.2c0 87.4-72.7 158.5-160.1 158.5c-26.6 0-52.7-6.7-75.8-19.3z" />
                    </svg>
                  </a>
                </div>
              ) : (
                <button
                  className="bg-[#503691] text-white px-4 py-2 rounded-full hover:bg-blue-600 transition-colors"
                  onClick={handleContactClick}
                >
                  Get Contact Number
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Contact Button */}
        <div className="flex flex-col lg:flex-2 lg:hidden justify-between gap-2 w-full">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 w-full">
            {/* Share */}
            <div className="flex flex-row items-center justify-center gap-2 h-fit hover:bg-white bg-[#503691] border border-[#503691] text-white hover:text-[#503691] px-4 md:px-6 py-1.5 rounded-lg cursor-pointer text-sm md:text-base" onClick={handleShareClick}>
              <FaShareAlt className="h-4 w-4" />
              Share
            </div>

            {/* Match Properties */}
            {/* <button
              className="flex flex-row items-center justify-center gap-2 h-fit bg-white text-green-600 hover:bg-green-600 hover:text-white border border-green-600 px-4 md:px-6 py-1.5 rounded-lg cursor-pointer text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handleMatchProperty(requirement?._id)}
            >
              <FaRegBookmark className="h-4 w-4" />
              Match Properties
            </button> */}

            {/* Edit */}
            <div
              className="flex flex-row items-center justify-center gap-2 h-fit bg-white text-[#503691] hover:bg-[#503691] hover:text-white border border-[#503691] px-4 md:px-6 py-1.5 rounded-lg cursor-pointer text-sm md:text-base"
              onClick={() => setIsEditing(!isEditing)}
            >
              <MdNoteAlt className="h-4 w-4" />
              {isEditing ? "Close" : "Edit"}
            </div>
            {isEditing && (
              <button
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                onClick={handleSaveRequirement}
              >
                Save
              </button>
            )}

            {/* Delete */}
            <div
              className="flex flex-row items-center justify-center gap-2 h-fit bg-white text-red-600 hover:bg-red-600 hover:text-white border border-red-600 px-4 md:px-6 py-1.5 rounded-lg cursor-pointer text-sm md:text-base"
              onClick={() => setShowDeleteModal(true)}
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </div>
          </div>
          {showContact && contactInfo ? (
            <div className="w-full flex items-center gap-3">
              {/* Contact Info */}
              <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex-1 min-w-0">
                <p className="text-green-700 font-medium text-sm truncate">
                  {!isEditing ? (
                    <p className="text-green-700 font-medium">
                      {requirement?.brokerName} - {requirement?.contactNumber} 📍
                    </p>
                  ) : (
                    <div className="flex flex-col items-end gap-2 w-full">
                      {/* Broker Name */}
                      <input
                        type="text"
                        className="border rounded px-2 py-1 w-full text-right"
                        placeholder="Broker Name"
                        value={editData.brokerName}
                        onChange={(e) =>
                          setEditData({ ...editData, brokerName: e.target.value })
                        }
                      />

                      {/* Contact Number */}
                      <input
                        type="text"
                        className="border rounded px-2 py-1 w-full text-right"
                        placeholder="Contact Number"
                        value={editData.contactNumber}
                        onChange={(e) =>
                          setEditData({ ...editData, contactNumber: e.target.value })
                        }
                      />
                    </div>
                  )}

                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                {/* Phone Link */}
                <a
                  href={`tel:${contactInfo.number}`}
                  className="flex items-center justify-center p-3 bg-blue-50 rounded-full text-[#503691] hover:bg-blue-100 transition-colors"
                  title="Call"
                >
                  <MdOutlinePhone className="h-5 w-5" />
                </a>

                {/* WhatsApp Link */}
                <a
                  href={`https://wa.me/${contactInfo.number}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center p-3 bg-green-50 rounded-full hover:bg-green-100 transition-colors"
                  title="WhatsApp"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 448 512"
                    className="h-5 w-5"
                    fill="#25D366"
                  >
                    <path d="M92.1 254.6c0 24.9 7 49.2 20.2 70.1l3.1 5-13.3 48.6L152 365.2l4.8 2.9c20.2 12 43.4 18.4 67.1 18.4h.1c72.6 0 133.3-59.1 133.3-131.8c0-35.2-15.2-68.3-40.1-93.2c-25-25-58-38.7-93.2-38.7c-72.7 0-131.8 59.1-131.9 131.8zM274.8 330c-12.6 1.9-22.4 .9-47.5-9.9c-36.8-15.9-61.8-51.5-66.9-58.7c-.4-.6-.7-.9-.8-1.1c-2-2.6-16.2-21.5-16.2-41c0-18.4 9-27.9 13.2-32.3c.3-.3 .5-.5 .7-.8c3.6-4 7.9-5 10.6-5c2.6 0 5.3 0 7.6 .1c.3 0 .5 0 .8 0c2.3 0 5.2 0 8.1 6.8c1.2 2.9 3 7.3 4.9 11.8c3.3 8 6.7 16.3 7.3 17.6c1 2 1.7 4.3 .3 6.9c-3.4 6.8-6.9 10.4-9.3 13c-3.1 3.2-4.5 4.7-2.3 8.6c15.3 26.3 30.6 35.4 53.9 47.1c4 2 6.3 1.7 8.6-1c2.3-2.6 9.9-11.6 12.5-15.5c2.6-4 5.3-3.3 8.9-2s23.1 10.9 27.1 12.9c.8 .4 1.5 .7 2.1 1c2.8 1.4 4.7 2.3 5.5 3.6c.9 1.9 .9 9.9-2.4 19.1c-3.3 9.3-19.1 17.7-26.7 18.8zM448 96c0-35.3-28.7-64-64-64H64C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V96zM148.1 393.9L64 416l22.5-82.2c-13.9-24-21.2-51.3-21.2-79.3C65.4 167.1 136.5 96 223.9 96c42.4 0 82.2 16.5 112.2 46.5c29.9 30 47.9 69.8 47.9 112.2c0 87.4-72.7 158.5-160.1 158.5c-26.6 0-52.7-6.7-75.8-19.3z" />
                  </svg>
                </a>
              </div>
            </div>
          ) : (
            <button
              className="w-full px-4 py-3 rounded-lg hover:bg-white bg-[#503691] border border-[#503691] text-white hover:text-[#503691] transition-colors text-sm font-medium"
              onClick={handleContactClick}
            >
              Get Contact Number
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md w-full">
      {/* Toast Notification */}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={closeToast} />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        status={newStatus}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteProperty}
      />

      {/* Desktop mode */}

      <div className="hidden lg:flex flex-wrap items-center justify-between gap-3 pt-4 px-2 xl:px-4">
        {/* Left: Date & ID */}
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <div className="relative group">
            <div
              className={`w-3 h-3 rounded-full ${property?.verifyStatus === "unverified"
                ? "bg-yellow-400"
                : "bg-green-500"
                }`}
            ></div>
            <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-max px-3 py-1 rounded bg-gray-800 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 whitespace-nowrap shadow-md">
              {property?.verifyStatus === "unverified"
                ? "This property is unverified"
                : "This property is verified"}
            </div>
          </div>

          <div className="rounded-full text-red-500 font-bold px-4 py-1 border border-red-500 whitespace-nowrap">
            {formatDate(property?.listedDate)}
          </div>
          <div className="rounded-full text-blue-600 font-bold px-4 py-1 border border-blue-600 whitespace-nowrap">
            ID: {property?.propertyId || "NA"}
          </div>
        </div>

        {/* Title & Rent */}
        <div className="flex flex-wrap items-center gap-2 min-w-0 max-w-full justify-center">

          {!isEditing ? (
            <p className="text-[#503691] bg-[#EFE9FF] font-bold px-4 py-2 rounded-full border border-blue-700 
                truncate max-w-[180px] sm:max-w-[220px] md:max-w-[260px] lg:max-w-[300px] 
                xl:max-w-[360px] cursor-pointer">
              Premise - {property?.title || "NA"}
            </p>
          ) : (
            <div className="flex items-center gap-2 bg-white border border-blue-700 px-3 py-2 rounded-full 
                  text-sm flex-1 min-w-0">

              {/* LABEL */}
              <span className="text-[#503691] font-bold whitespace-nowrap">
                Premise:
              </span>

              {/* INPUT */}
              <input
                type="text"
                className="border rounded px-2 py-1 w-full text-right"
                value={editData.title}
                onChange={(e) =>
                  setEditData({ ...editData, title: e.target.value })
                }
                placeholder="Enter Premise Name"
              />
            </div>
          )}



          {!isEditing ? (
            // VIEW MODE (Both Broker & Builder)
            <p className="text-[#503691] bg-[#EFE9FF] font-bold px-3 py-2 rounded-full border border-blue-700 text-sm truncate flex-1 text-center min-w-0">

              {/* Builder properties */}
              {property?.listedBy === "builders" ? (
                property?.priceRange?.min && property?.priceRange?.max ? (
                  `Price: ${formatPrice(property.priceRange.min)} - ${formatPrice(property.priceRange.max)}`
                ) : (
                  "Price on Request"
                )
              ) : property?.type?.includes("Rent") ? (
                `Rent: ${formatPrice(property?.price)}`
              ) : property?.type?.includes("Sell") ? (
                `Sell: ${formatPrice(property?.price)}`
              ) : (
                `Price: ${formatPrice(property?.price)}`
              )}
            </p>

          ) : (
            // EDIT MODE (Combined)
            <div className="flex items-center gap-2 bg-white border border-blue-700 px-3 py-2 rounded-full text-sm flex-1 min-w-0">

              {/* LABEL */}
              <span className="text-[#503691] font-bold whitespace-nowrap">
                {property?.listedBy === "builders"
                  ? "Price:"
                  : property?.type?.includes("Rent")
                    ? "Rent:"
                    : property?.type?.includes("Sell")
                      ? "Sell:"
                      : "Price:"}
              </span>

              {/* BUILDER — MIN PRICE */}
              {property?.listedBy === "builders" ? (
                <>
                  <input
                    type="number"
                    placeholder="Min"
                    className="border rounded px-2 py-1 w-20 text-center"
                    value={editData?.priceRange?.min ?? ""}
                    onChange={(e) =>
                      setEditData({
                        ...editData,
                        priceRange: {
                          ...editData.priceRange,
                          min: e.target.value,
                        },
                      })
                    }
                  />

                  <span>-</span>

                  {/* BUILDER — MAX PRICE */}
                  <input
                    type="number"
                    placeholder="Max"
                    className="border rounded px-2 py-1 w-20 text-center"
                    value={editData?.priceRange?.max ?? ""}
                    onChange={(e) =>
                      setEditData({
                        ...editData,
                        priceRange: {
                          ...editData.priceRange,
                          max: e.target.value,
                        },
                      })
                    }
                  />
                </>
              ) : (
                // NORMAL PRICE (SINGLE INPUT)
                <input
                  type="number"
                  placeholder="0"
                  className="border rounded px-2 py-1 w-24 text-center"
                  value={editData.price}
                  onChange={(e) =>
                    setEditData({ ...editData, price: e.target.value })
                  }
                />
              )}
            </div>
          )}

        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          {/* Status Dropdown */}
          <StatusDropdown
            status={currentStatus}
            onChange={handleStatusChange}
            propertyType={requirement?.type}
          />

          {isEditing ? (
            <div
              className="flex items-center gap-2 border border-[#503691] px-3 py-1.5 rounded-lg cursor-pointer bg-white text-[#503691] hover:bg-[#503691] hover:text-white"
              onClick={handleMediaButtonClick}
            >
              <FaCameraRetro className="h-4 w-4" />
              Upload Media
              <input
                ref={mediaInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                className="hidden"
                onChange={handleMediaInputChange}
              />
            </div>
          ) : property?.media?.length > 0 ? (
            <Photos_Video images={property.media.map((item) => item.url)} />
          ) : (
            <div className="flex items-center gap-2 bg-gray-100 hover:bg-white text-gray-400 hover:text-blue-800 border border-gray-300 px-3 py-1.5 rounded-lg cursor-not-allowed">
              <FaCameraRetro className="h-4 w-4 text-gray-400" />
              Media
            </div>
          )}

          {isEditing && (editData?.media?.length || 0) > 0 && (
            <div
              className="flex items-center gap-2 border border-[#503691] px-3 py-1.5 rounded-lg cursor-pointer bg-white text-[#503691] hover:bg-[#503691] hover:text-white"
              onClick={() => setShowMediaOverlay(true)}
              title="Show all media"
            >
              Show All Media
            </div>
          )}

          <div className="flex items-center gap-2 bg-[#503691] hover:bg-white text-white hover:text-blue-800 border border-blue-800 px-3 h-10 rounded-lg cursor-pointer" onClick={handleShareClick}>
            <FaShareAlt className="h-4 w-4" />
          </div>

          {/* Edit button */}
          <div
            className="flex items-center gap-2 border border-[#503691] px-3 py-2.5 rounded-lg cursor-pointer bg-white text-[#503691] hover:bg-[#503691] hover:text-white"
            onClick={() => {

              setEditData((prev) => ({
                ...prev,
                name: property?.name || "",
                number: property?.number || "",
              }));
              setIsEditing(!isEditing)
            }}
          >
            <MdNoteAlt className="h-4 w-4" />
            {isEditing ? "Close" : "Edit"}
          </div>

          {isEditing && (
            <button
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              onClick={handleSaveProperty}
            >
              Save
            </button>
          )}

          {/* Delete button */}
          <div
            className="flex items-center gap-2 border border-red-600 px-3 py-2.5 rounded-lg cursor-pointer bg-white text-red-600 hover:bg-red-600 hover:text-white"
            onClick={() => setShowDeleteModal(true)}
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </div>
        </div>

        {isEditing && showMediaOverlay && (editData?.media?.length || 0) > 0 && (
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-1/2 h-1/1 bg-white border border-gray-300 rounded-xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between p-3 border-b">
              <div className="font-semibold text-gray-800 text-sm">Select Media</div>
              <button
                className="px-2 py-1 rounded hover:bg-gray-100 text-gray-700 text-sm"
                onClick={() => setShowMediaOverlay(false)}
              >
                ×
              </button>
            </div>
            <div className="p-3 overflow-y-auto h-[calc(100%-96px)]">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {editData.media.map((m, idx) => {
                  const url = getMediaUrl(m);
                  return (
                    <div
                      key={url || idx}
                      className={`relative rounded-xl overflow-hidden border border-gray-200 group`}
                    >
                      <div className="aspect-square bg-gray-100 flex items-center justify-center relative">
                        {m.type === "video" || (typeof url === 'string' && /\.(mp4|webm|ogg)$/i.test(url)) ? (
                          <video src={url} className="w-full h-full object-cover" controls />
                        ) : (
                          <img src={url} alt="" className="w-full h-full object-cover" />
                        )}
                      </div>
                      <button
                        className="absolute top-2 right-2 px-2 py-1 rounded text-xs bg-red-600 text-white hover:bg-red-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveMedia(url);
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="p-3 border-t flex items-center justify-end gap-2">
              {imageLimitExceeded && (
                <span className="text-red-600 text-sm">Only 10 media files allowed</span>
              )}
              <button
                className={`px-4 py-2 rounded-lg border ${imageLimitExceeded ? "bg-gray-200 text-gray-500 border-gray-300 pointer-events-none opacity-60" : imagesConfirmed ? "bg-green-600 text-white border-green-700" : "bg-white text-[#503691] border-[#503691] hover:bg-[#503691] hover:text-white"}`}
                disabled={imageLimitExceeded}
                onClick={handleConfirmImages}
              >
                {imagesConfirmed ? "Confirmed" : "Confirm Media"}
              </button>
            </div>
          </div>
        )}

      </div>

      {/* Mobile mode */}
      <div className="lg:hidden bg-white rounded-lg p-4 border border-gray-200 shadow-sm overflow-hidden">
        {/* Main Content Container */}
        <div className="flex flex-col gap-3 w-full">
          {/* First Row: Property Name and Price */}
          <div className="flex flex-row justify-between gap-2 w-full">

            {/* --------------------- PREMISSE BLOCK ---------------------- */}
            {!isEditing ? (
              <p className="text-[#503691] bg-[#EFE9FF] font-bold 
                  px-4 py-2 rounded-full border border-blue-700 
                  truncate w-full 
                  cursor-pointer text-sm sm:text-base text-center
                  flex-1 min-w-0">
                Premise - {property?.title || "NA"}
              </p>
            ) : (
              <div className="flex flex-col sm:flex-row 
                    items-start sm:items-center 
                    gap-2 
                    bg-white border border-blue-700 
                    px-4 py-3 rounded-xl 
                    text-sm sm:text-base 
                    w-full flex-1 min-w-0">

                {/* LABEL */}
                <span className="text-[#503691] font-bold whitespace-nowrap 
                       text-sm sm:text-base">
                  Premise:
                </span>

                {/* INPUT */}
                <input
                  type="text"
                  className="border rounded-lg 
                   px-3 py-2 
                   w-full sm:flex-1  
                   text-sm sm:text-base 
                   h-[40px] sm:h-[32px] text-center"
                  value={editData.title}
                  onChange={(e) =>
                    setEditData({ ...editData, title: e.target.value })
                  }
                  placeholder="Enter Premise Name"
                />
              </div>
            )}

            {/* ------------------------- PRICE BLOCK --------------------------- */}
            {!isEditing ? (
              <p className="text-[#503691] bg-[#EFE9FF] font-bold px-3 py-2 rounded-full border border-blue-700 text-sm truncate flex-1 text-center min-w-0">
                {/* Builder properties */}
                {property?.listedBy === "builders" ? (
                  property?.priceRange?.min && property?.priceRange?.max ? (
                    `Price: ${formatPrice(property.priceRange.min)} - ${formatPrice(property.priceRange.max)}`
                  ) : (
                    "Price on Request"
                  )
                ) : property?.type?.includes("Rent") ? (
                  `Rent: ${formatPrice(property?.price)}`
                ) : property?.type?.includes("Sell") ? (
                  `Sell: ${formatPrice(property?.price)}`
                ) : (
                  `Price: ${formatPrice(property?.price)}`
                )}
              </p>
            ) : (
              <div className="flex flex-col sm:flex-row 
                    items-start sm:items-center 
                    gap-2 bg-white border border-blue-700 
                    px-4 py-3 rounded-xl 
                    text-sm sm:text-base 
                    w-full flex-1 min-w-0">

                {/* LABEL */}
                <span className="text-[#503691] font-bold whitespace-nowrap">
                  {property?.listedBy === "builders"
                    ? "Price:"
                    : property?.type?.includes("Rent")
                      ? "Rent:"
                      : property?.type?.includes("Sell")
                        ? "Sell:"
                        : "Price:"}
                </span>

                {/* BUILDER PRICE RANGE */}
                {property?.listedBy === "builders" ? (
                  <div className="flex flex-col sm:flex-row w-full gap-2">

                    {/* MIN INPUT */}
                    <input
                      type="number"
                      placeholder="Min"
                      className="border rounded-lg 
                       px-3 py-2 sm:px-2 sm:py-1 
                       text-center 
                       w-full sm:w-24"
                      value={editData?.priceRange?.min}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          priceRange: {
                            ...editData.priceRange,
                            min: e.target.value,
                          },
                        })
                      }
                    />

                    {/* Hyphen only on desktop */}
                    <span className="hidden sm:block font-bold">-</span>

                    {/* MAX INPUT */}
                    <input
                      type="number"
                      placeholder="Max"
                      className="border rounded-lg 
                       px-3 py-2 sm:px-2 sm:py-1 
                       text-center 
                       w-full sm:w-24"
                      value={editData?.priceRange?.max}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          priceRange: {
                            ...editData.priceRange,
                            max: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                ) : (
                  /* NORMAL PRICE */
                  <input
                    type="number"
                    placeholder="0"
                    className="border rounded-lg 
                     px-3 py-2 sm:px-2 sm:py-1 
                     text-center 
                     w-full sm:w-24"
                    value={editData.price}
                    onChange={(e) =>
                      setEditData({ ...editData, price: e.target.value })
                    }
                  />
                )}
              </div>
            )}

          </div>


          {/* Second Row: Verification, Date, ID and Status */}
          <div className="flex flex-row justify-between items-center gap-2 w-full">
            {/* Left Side: Verification, Date, ID */}
            <div className="flex flex-row gap-2 items-center flex-1 min-w-0">
              {/* Verification Dot */}
              <div className="relative group flex-shrink-0">
                <div
                  className={`w-3 h-3 rounded-full ${property?.verifyStatus === "unverified"
                    ? "bg-yellow-400"
                    : "bg-green-500"
                    }`}
                ></div>
              </div>

              {/* Date */}
              <div className="rounded-full bg-gray-100 text-sm text-red-500 font-bold px-3 py-1 whitespace-nowrap flex-shrink-0">
                {formatDate(property?.listedDate)}
              </div>

              {/* ID */}
              <div className="rounded-full bg-gray-100 text-sm text-blue-600 font-bold px-3 py-1 whitespace-nowrap  flex-shrink-0">
                ID: {property?.propertyId || "NA"}
              </div>
            </div>

            {/* Right Side: Status Dropdown */}
            <div className="flex-shrink-0">
              <StatusDropdown
                status={currentStatus}
                onChange={handleStatusChange}
                propertyType={property?.type}
              />
            </div>
          </div>
        </div>
      </div>

      {isEditing && (
        <div className="lg:hidden flex items-center gap-2 mt-2">
          <div
            className="flex items-center gap-2 border border-[#503691] px-3 py-1.5 rounded-lg cursor-pointer bg-white text-[#503691] hover:bg-[#503691] hover:text-white"
            onClick={handleMediaButtonClick}
          >
            <FaCameraRetro className="h-4 w-4" />
            Upload Image
            <input
              ref={mediaInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              className="hidden"
              onChange={handleMediaInputChange}
            />
          </div>
          {(editData?.media?.length || 0) > 0 && (
            <>
              <button
                className="flex items-center gap-2 border border-[#503691] px-3 py-1.5 rounded-lg cursor-pointer bg-white text-[#503691] hover:bg-[#503691] hover:text-white"
                onClick={() => setShowMediaOverlay(true)}
              >
                Show All Media
              </button>
            </>
          )}
        </div>
      )}

      {/* Thumbnails are only shown inside overlay; hidden outside */}

      {/* Main content area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 text-sm lg:text-md gap-4 p-2 lg:p-4 divide-x lg:divide-solid divide-none lg:divide-black">
        {/* First grid of items */}
        <div className="grid grid-cols-1 gap-2">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-row items-center">
              <svg
                className="mr-2 h-6 w-6"
                width="33"
                height="32"
                viewBox="0 0 33 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.37089 0.15625L6.2147 0.30625V15.5875V30.8687L3.72184 30.8875L1.22897 30.9062L1.06653 31.0562C0.847861 31.2625 0.841613 31.6687 1.04779 31.8625L1.19149 32H16.9046H32.624L32.7552 31.8563C32.9552 31.6437 32.9427 31.2562 32.7303 31.0562C32.5678 30.9062 32.5678 30.9062 30.8872 30.8875L29.2065 30.8687V22.1C29.2065 15.7188 29.1878 13.275 29.1378 13.125C29.0003 12.7437 29.0441 12.75 24.9268 12.75H21.2094V6.63125C21.2094 2.54375 21.1906 0.449999 21.1469 0.3375C21.0094 -0.0125008 21.4343 0.00625038 13.6933 0H6.52084L6.37089 0.15625ZM20.0848 6.9375V12.7437L17.2983 12.7625L14.5117 12.7812L14.3618 12.9562L14.2119 13.1313V22.0062V30.875H13.7183H13.2185L13.1997 28.2938C13.181 25.7375 13.181 25.7188 13.0373 25.425C12.8498 25.0437 12.4562 24.7125 12.0626 24.5938C11.694 24.4812 10.4632 24.4688 10.0696 24.5687C9.66347 24.6687 9.25737 25 9.06369 25.3812L8.90124 25.7125V28.2938V30.875H8.12027H7.3393V16V1.125H13.712H20.0848V6.9375ZM28.0819 22.375V30.875H21.7092H15.3365V22.375V13.875H21.7092H28.0819V22.375ZM11.9127 25.8L12.0564 25.9437L12.0751 28.4062L12.0939 30.875H11.0567H10.0258V28.4188C10.0258 26.5437 10.0446 25.9375 10.1071 25.8625C10.2695 25.6625 10.4319 25.625 11.1005 25.6375C11.7065 25.6562 11.7877 25.6687 11.9127 25.8Z"
                  fill="#F1D35C"
                />
                <path
                  d="M9.06369 2.25625L8.90125 2.38125V4.21875C8.90125 5.225 8.91999 6.1 8.93873 6.15625C9.0387 6.40625 9.25737 6.4375 11.0192 6.4375C11.9439 6.4375 12.7749 6.4125 12.8623 6.375C13.1935 6.25 13.2185 6.09375 13.1997 4.1875L13.181 2.45L13.031 2.2875L12.8811 2.125H11.0567C9.3011 2.125 9.22613 2.13125 9.06369 2.25625ZM12.0876 4.28125V5.3125H11.0567H10.0258V4.28125V3.25H11.0567H12.0876V4.28125Z"
                  fill="#F1D35C"
                />
                <path
                  d="M14.3681 2.28125L14.2119 2.43125V4.24375V6.05625L14.3619 6.23125L14.5118 6.40625H16.3674H18.223L18.3729 6.23125L18.5229 6.05625V4.25625V2.45625L18.3667 2.2875L18.2167 2.125H16.3674H14.5181L14.3681 2.28125ZM17.3983 4.28125V5.3125H16.3674H15.3365V4.28125V3.25H16.3674H17.3983V4.28125Z"
                  fill="#F1D35C"
                />
                <path
                  d="M9.1824 7.5125C8.90749 7.6625 8.90125 7.6875 8.90125 9.60625V11.4437L9.05744 11.5938L9.20739 11.75H11.0442H12.8811L13.031 11.5875L13.181 11.425V9.58125V7.7375L13.006 7.5875L12.8311 7.4375H11.0692C9.91338 7.44375 9.26362 7.4625 9.1824 7.5125ZM12.0876 9.59375V10.625H11.0567H10.0258V9.59375V8.5625H11.0567H12.0876V9.59375Z"
                  fill="#F1D35C"
                />
                <path
                  d="M14.4931 7.5125C14.2182 7.66875 14.2182 7.7 14.2119 9.6V11.4188L14.3744 11.5875L14.543 11.75H16.3674H18.1917L18.3604 11.5875L18.5229 11.4188V9.65C18.5229 7.775 18.5041 7.63125 18.2292 7.50625C18.0168 7.4125 14.6618 7.4125 14.4931 7.5125ZM17.3983 9.59375V10.625H16.3674H15.3365V9.59375V8.5625H16.3674H17.3983V9.59375Z"
                  fill="#F1D35C"
                />
                <path
                  d="M9.27611 12.7938C9.22613 12.8125 9.11992 12.8875 9.04494 12.9563C8.90125 13.0813 8.90125 13.0938 8.90125 14.9375V16.7938L9.06369 16.9438C9.22613 17.0938 9.22613 17.0938 10.8693 17.1125C11.9439 17.125 12.6062 17.1063 12.7749 17.0625C13.1935 16.95 13.2122 16.8438 13.2122 14.9375C13.2122 13.1 13.181 12.9438 12.8373 12.8188C12.6249 12.75 9.46354 12.725 9.27611 12.7938ZM12.0876 14.9375V16H11.0567H10.0258V14.9375V13.875H11.0567H12.0876V14.9375Z"
                  fill="#F1D35C"
                />
                <path
                  d="M9.06369 18.2562L8.90125 18.3812V20.2188C8.90125 21.225 8.91999 22.1 8.93873 22.1562C9.0387 22.4062 9.25737 22.4375 11.0192 22.4375C11.9439 22.4375 12.7749 22.4125 12.8623 22.375C13.1935 22.25 13.2185 22.0938 13.1997 20.1875L13.181 18.45L13.031 18.2875L12.8811 18.125H11.0567C9.3011 18.125 9.22613 18.1312 9.06369 18.2562ZM12.0876 20.2812V21.3125H11.0567H10.0258V20.2812V19.25H11.0567H12.0876V20.2812Z"
                  fill="#F1D35C"
                />
                <path
                  d="M17.3045 14.9187C17.2358 14.9374 17.1171 15.0062 17.0421 15.0812C16.8984 15.2062 16.8984 15.2187 16.8984 17.0624V18.9187L17.0609 19.0687C17.2233 19.2187 17.2233 19.2187 18.8852 19.2374C19.9973 19.2499 20.6346 19.2374 20.7908 19.1874C21.1844 19.0749 21.2094 18.9437 21.2094 17.0624C21.2094 15.1749 21.1844 15.0499 20.7908 14.9437C20.5596 14.8749 17.5607 14.8562 17.3045 14.9187ZM20.0848 17.0624V18.1249H19.0539H18.023V17.0624V15.9999H19.0539H20.0848V17.0624Z"
                  fill="#F1D35C"
                />
                <path
                  d="M22.5401 14.9688C22.4339 15.0125 22.3152 15.1062 22.2777 15.1813C22.234 15.2625 22.209 15.9312 22.209 17.0875V18.8688L22.3589 19.0438L22.5089 19.2188H24.3645H26.2201L26.37 19.0438L26.5199 18.8688V17.0875C26.5199 15.9312 26.495 15.2625 26.4512 15.175C26.3075 14.9125 26.0639 14.875 24.352 14.875C23.1212 14.8812 22.6901 14.9 22.5401 14.9688ZM25.3953 17.0625V18.125H24.3645H23.3336V17.0625V16H24.3645H25.3953V17.0625Z"
                  fill="#F1D35C"
                />
                <path
                  d="M17.0546 20.4062L16.8984 20.5563V22.3687C16.8984 23.3625 16.9172 24.225 16.9359 24.2812C17.0359 24.5312 17.2483 24.5625 19.0789 24.5625H20.8283L21.0032 24.4125L21.1782 24.2625V22.4188V20.575L21.0282 20.4125L20.8783 20.25H19.0414H17.2046L17.0546 20.4062ZM20.0848 22.4062V23.4375H19.0539H18.023V22.4062V21.375H19.0539H20.0848V22.4062Z"
                  fill="#F1D35C"
                />
                <path
                  d="M22.3714 20.4125L22.209 20.5812V22.4C22.2152 24.3125 22.2152 24.3375 22.5026 24.4875C22.6901 24.5875 26.0139 24.5937 26.2263 24.4937C26.5012 24.3687 26.5199 24.225 26.5199 22.35V20.5812L26.3575 20.4125L26.1888 20.25H24.3645H22.5401L22.3714 20.4125ZM25.3953 22.4062V23.4375H24.3645H23.3336V22.4062V21.375H24.3645H25.3953V22.4062Z"
                  fill="#F1D35C"
                />
                <path
                  d="M17.1796 25.6375C16.9047 25.7875 16.8984 25.8125 16.8984 27.7563V29.6188L17.0609 29.7438C17.2233 29.8688 17.2983 29.875 19.0539 29.875H20.8783L21.0282 29.7125L21.1782 29.55L21.1969 27.8125C21.2157 25.9063 21.1907 25.75 20.8595 25.625C20.6346 25.5375 17.3358 25.55 17.1796 25.6375ZM20.0848 27.7188V28.75H19.0539H18.023V27.7188V26.6875H19.0539H20.0848V27.7188Z"
                  fill="#F1D35C"
                />
                <path
                  d="M22.5464 25.625C22.2215 25.7562 22.209 25.8312 22.209 27.7687V29.5687L22.3652 29.7188L22.5151 29.875H24.3645H26.2138L26.3638 29.7125L26.5199 29.5437V27.7437V25.9437L26.37 25.7687L26.2201 25.5938L24.4457 25.5812C23.4773 25.575 22.6151 25.5938 22.5464 25.625ZM25.3953 27.7188V28.75H24.3645H23.3336V27.7188V26.6875H24.3645H25.3953V27.7188Z"
                  fill="#F1D35C"
                />
              </svg>
              <p>Type</p>
            </div>
            {!isEditing ? (
              <p className="text-end">{property?.type || "NA"}</p>
            ) : (
              <select
                className="border rounded px-2 py-1 text-end"
                value={editData.type}
                onChange={(e) =>
                  setEditData({ ...editData, type: e.target.value })
                }
              >
                <option value="">Select Type</option>

                {/* If listed by builder → show only Residential / Commercial */}
                {property?.listedBy === "builders" ? (
                  <>
                    <option value="Residential">Residential</option>
                    <option value="Commercial">Commercial</option>
                  </>
                ) : (
                  /* Normal broker options */
                  <>
                    <option value="Residential Rent">Residential Rent</option>
                    <option value="Residential Sell">Residential Sell</option>
                    <option value="Commercial Rent">Commercial Rent</option>
                    <option value="Commercial Sell">Commercial Sell</option>
                  </>
                )}
              </select>
            )}

          </div>
          <hr className="block"></hr>

          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-row items-center">
              <svg
                className="mr-2 h-6 w-6"
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.7377 1.22155C12.2465 1.88853 10.9665 2.50304 10.8873 2.57799C10.7487 2.72038 10.7487 2.72038 10.7487 8.74567V14.7635H6.17613C1.82128 14.7635 1.59034 14.771 1.49137 14.8984C1.3858 15.0258 1.3792 15.6927 1.3792 22.9546V30.8759H0.831545C0.343274 30.8759 0.264095 30.8984 0.138728 31.0482C-0.0526211 31.2806 -0.0460228 31.5878 0.158523 31.8126L0.316881 32H15.9811H31.6454L31.8235 31.8426C32.0413 31.6478 32.0611 31.2506 31.8631 31.0258C31.7509 30.8984 31.6454 30.8759 31.2033 30.8759H30.6754L30.6622 22.7447C30.6424 14.7335 30.6424 14.6136 30.5105 14.4712C30.4379 14.3888 29.2964 13.6019 27.9701 12.7251L25.5618 11.1288L24.4269 11.1063L23.2854 11.0838V7.23185V3.37986L23.1402 3.2C23.0413 3.08009 21.913 2.50304 19.8081 1.50632C18.0662 0.681969 16.5948 0 16.542 0C16.4958 0.00749397 15.2355 0.554567 13.7377 1.22155ZM16.0933 16.1499V30.8759H13.9159H11.7385V17.1016V3.3274L13.8367 2.38314C14.9848 1.86604 15.9679 1.43888 16.0141 1.43138C16.0735 1.42389 16.0933 4.41405 16.0933 16.1499ZM19.7421 2.70539L22.2957 3.91944V17.4014V30.8759H19.6564H17.0171V16.1424C17.0171 4.38408 17.0368 1.41639 17.1028 1.44637C17.1424 1.46885 18.3301 2.03091 19.7421 2.70539ZM24.869 21.5457V30.8759H24.0772H23.2854V21.5457V12.2155H24.0772H24.869V21.5457ZM27.924 14.0141L29.6857 15.1756V23.022V30.8759H27.7722H25.8587V21.7705V12.6576L26.0171 12.755C26.0962 12.8075 26.9606 13.3771 27.924 14.0141ZM10.7487 23.3443V30.8759H6.55883H2.36894V23.3443V15.8126H6.55883H10.7487V23.3443Z"
                  fill="#249B7E"
                />
                <path
                  d="M14.6087 3.78455C14.4569 3.94942 14.4437 4.02436 14.4437 4.64638C14.4437 5.26839 14.4569 5.34333 14.6087 5.5082C14.6945 5.61312 14.8462 5.69556 14.9386 5.69556C15.031 5.69556 15.1827 5.61312 15.2685 5.5082C15.4203 5.34333 15.4335 5.26839 15.4335 4.66136C15.4335 4.0993 15.4137 3.96441 15.2949 3.79204C15.1167 3.53724 14.8264 3.52975 14.6087 3.78455Z"
                  fill="#249B7E"
                />
                <path
                  d="M12.5303 4.34669C12.4181 4.47409 12.3983 4.594 12.3983 5.24599C12.3983 6.09282 12.4907 6.29517 12.8602 6.29517C13.2297 6.29517 13.3221 6.09282 13.3221 5.24599C13.3221 4.39915 13.2297 4.19681 12.8602 4.19681C12.748 4.19681 12.6029 4.26425 12.5303 4.34669Z"
                  fill="#249B7E"
                />
                <path
                  d="M14.6087 7.68152C14.4569 7.84639 14.4437 7.92134 14.4437 8.54335C14.4437 9.16536 14.4569 9.2403 14.6087 9.40518C14.6945 9.51009 14.8462 9.59253 14.9386 9.59253C15.031 9.59253 15.1827 9.51009 15.2685 9.40518C15.4203 9.2403 15.4335 9.16536 15.4335 8.54335C15.4335 7.92134 15.4203 7.84639 15.2685 7.68152C15.1827 7.5766 15.031 7.49417 14.9386 7.49417C14.8462 7.49417 14.6945 7.5766 14.6087 7.68152Z"
                  fill="#249B7E"
                />
                <path
                  d="M12.5303 8.24366C12.4181 8.37106 12.3983 8.49097 12.3983 9.14296C12.3983 9.9898 12.4907 10.1921 12.8602 10.1921C13.2297 10.1921 13.3221 9.9898 13.3221 9.14296C13.3221 8.29612 13.2297 8.09378 12.8602 8.09378C12.748 8.09378 12.6029 8.16123 12.5303 8.24366Z"
                  fill="#249B7E"
                />
                <path
                  d="M14.6087 11.5785C14.4569 11.7434 14.4437 11.8183 14.4437 12.4403C14.4437 13.0623 14.4569 13.1373 14.6087 13.3021C14.6945 13.4071 14.8462 13.4895 14.9386 13.4895C15.031 13.4895 15.1827 13.4071 15.2685 13.3021C15.4203 13.1373 15.4335 13.0623 15.4335 12.4403C15.4335 11.8183 15.4203 11.7434 15.2685 11.5785C15.1827 11.4736 15.031 11.3911 14.9386 11.3911C14.8462 11.3911 14.6945 11.4736 14.6087 11.5785Z"
                  fill="#249B7E"
                />
                <path
                  d="M12.5303 12.1406C12.4181 12.268 12.3983 12.3879 12.3983 13.0399C12.3983 13.8868 12.4907 14.0891 12.8602 14.0891C13.2297 14.0891 13.3221 13.8868 13.3221 13.0399C13.3221 12.1931 13.2297 11.9908 12.8602 11.9908C12.748 11.9908 12.6029 12.0582 12.5303 12.1406Z"
                  fill="#249B7E"
                />
                <path
                  d="M14.6087 15.4755C14.4569 15.6403 14.4437 15.7153 14.4437 16.3373C14.4437 16.9593 14.4569 17.0342 14.6087 17.1991C14.6945 17.304 14.8462 17.3865 14.9386 17.3865C15.031 17.3865 15.1827 17.304 15.2685 17.1991C15.4203 17.0342 15.4335 16.9593 15.4335 16.3373C15.4335 15.7153 15.4203 15.6403 15.2685 15.4755C15.1827 15.3705 15.031 15.2881 14.9386 15.2881C14.8462 15.2881 14.6945 15.3705 14.6087 15.4755Z"
                  fill="#249B7E"
                />
                <path
                  d="M12.6622 15.9401C12.4445 16.03 12.3983 16.2173 12.3983 16.9518C12.3983 17.7836 12.4907 17.986 12.8602 17.986C13.2297 17.986 13.3221 17.7836 13.3221 16.9518C13.3221 16.1799 13.3023 16.1049 13.0779 15.97C12.9262 15.8801 12.8206 15.8726 12.6622 15.9401Z"
                  fill="#249B7E"
                />
                <path
                  d="M14.6087 19.3723C14.4569 19.5372 14.4437 19.6121 14.4437 20.2192C14.4437 21.021 14.5625 21.2833 14.9386 21.2833C15.3147 21.2833 15.4335 21.021 15.4335 20.2192C15.4335 19.6121 15.4203 19.5372 15.2685 19.3723C15.1827 19.2674 15.031 19.185 14.9386 19.185C14.8462 19.185 14.6945 19.2674 14.6087 19.3723Z"
                  fill="#249B7E"
                />
                <path
                  d="M12.6293 19.8745C12.4181 20.0094 12.3983 20.0843 12.3983 20.8487C12.3983 21.6806 12.4907 21.8829 12.8602 21.8829C13.2297 21.8829 13.3221 21.6806 13.3221 20.8487C13.3221 20.0768 13.3023 20.0019 13.0779 19.867C12.8998 19.7621 12.8008 19.7621 12.6293 19.8745Z"
                  fill="#249B7E"
                />
                <path
                  d="M14.6087 23.2693C14.4569 23.4342 14.4437 23.5091 14.4437 24.1161C14.4437 24.918 14.5625 25.1803 14.9386 25.1803C15.3147 25.1803 15.4335 24.918 15.4335 24.1161C15.4335 23.5091 15.4203 23.4342 15.2685 23.2693C15.1827 23.1644 15.031 23.0819 14.9386 23.0819C14.8462 23.0819 14.6945 23.1644 14.6087 23.2693Z"
                  fill="#249B7E"
                />
                <path
                  d="M12.6293 23.7715C12.4181 23.9064 12.3983 23.9813 12.3983 24.7832C12.3983 25.4876 12.4115 25.5551 12.5435 25.66C12.6227 25.7274 12.7678 25.7799 12.8602 25.7799C12.9526 25.7799 13.0977 25.7274 13.1769 25.66C13.3089 25.5551 13.3221 25.4876 13.3221 24.7832C13.3221 23.9663 13.3023 23.8989 13.0779 23.764C12.8998 23.6591 12.8008 23.6591 12.6293 23.7715Z"
                  fill="#249B7E"
                />
                <path
                  d="M14.6087 27.1663C14.4569 27.3312 14.4437 27.4061 14.4437 28.0132C14.4437 28.5752 14.4635 28.7101 14.5823 28.8825C14.7604 29.1373 15.0904 29.1523 15.2883 28.905C15.4137 28.7551 15.4335 28.6427 15.4335 28.0356C15.4335 27.4061 15.4203 27.3312 15.2685 27.1663C15.1827 27.0614 15.031 26.979 14.9386 26.979C14.8462 26.979 14.6945 27.0614 14.6087 27.1663Z"
                  fill="#249B7E"
                />
                <path
                  d="M12.5699 27.7358C12.4049 27.8857 12.3983 27.9156 12.3983 28.665C12.3983 29.3845 12.4115 29.4519 12.5435 29.5569C12.6227 29.6243 12.7678 29.6768 12.8602 29.6768C12.9526 29.6768 13.0977 29.6243 13.1769 29.5569C13.3089 29.4519 13.3221 29.3845 13.3221 28.665C13.3221 27.9156 13.3155 27.8857 13.1505 27.7358C13.0515 27.6458 12.9262 27.5784 12.8602 27.5784C12.7942 27.5784 12.6688 27.6458 12.5699 27.7358Z"
                  fill="#249B7E"
                />
                <path
                  d="M17.8946 3.14748C17.6702 3.2524 17.6108 3.46223 17.6108 4.18917C17.6108 4.98354 17.7032 5.1709 18.0991 5.1709C18.4686 5.1709 18.6006 4.9086 18.6006 4.18917C18.6006 3.4997 18.5214 3.25989 18.2509 3.14748C18.1453 3.11001 18.0529 3.07254 18.0463 3.08003C18.0397 3.08003 17.9737 3.11001 17.8946 3.14748Z"
                  fill="#249B7E"
                />
                <path
                  d="M19.2935 3.93439C19.1417 4.09926 19.1285 4.1742 19.1285 4.78123C19.1285 5.34329 19.1483 5.47818 19.2671 5.65055C19.4453 5.90535 19.7752 5.92034 19.9731 5.67303C20.0985 5.51565 20.1183 5.41823 20.1183 4.78123C20.1183 4.09177 20.1117 4.0543 19.9467 3.90441C19.7158 3.69458 19.4914 3.70956 19.2935 3.93439Z"
                  fill="#249B7E"
                />
                <path
                  d="M20.844 4.57154C20.7318 4.69894 20.712 4.81885 20.712 5.47084C20.712 6.31768 20.8044 6.52002 21.1739 6.52002C21.5434 6.52002 21.6358 6.31768 21.6358 5.47084C21.6358 4.624 21.5434 4.42166 21.1739 4.42166C21.0617 4.42166 20.9166 4.48911 20.844 4.57154Z"
                  fill="#249B7E"
                />
                <path
                  d="M17.789 7.11943C17.6174 7.27681 17.6108 7.29929 17.6108 8.02622C17.6108 8.67072 17.6306 8.79063 17.7428 8.91803C17.9408 9.14285 18.2905 9.12037 18.4686 8.86557C18.594 8.67821 18.6072 8.58079 18.5874 7.96627C18.5676 7.20936 18.5082 7.08196 18.1717 6.99953C18.0265 6.96955 17.921 6.99953 17.789 7.11943Z"
                  fill="#249B7E"
                />
                <path
                  d="M19.2935 7.83132C19.1417 7.99619 19.1285 8.07114 19.1285 8.69315C19.1285 9.31516 19.1417 9.39011 19.2935 9.55498C19.4914 9.7798 19.7158 9.79479 19.9467 9.58495C20.1117 9.43507 20.1183 9.40509 20.1183 8.69315C20.1183 7.98121 20.1117 7.95123 19.9467 7.80135C19.7158 7.59151 19.4914 7.6065 19.2935 7.83132Z"
                  fill="#249B7E"
                />
                <path
                  d="M20.844 8.46841C20.7318 8.59581 20.712 8.71572 20.712 9.36021C20.712 10.1621 20.7582 10.297 21.0749 10.3869C21.2267 10.4319 21.4972 10.312 21.583 10.1621C21.6094 10.1171 21.6358 9.74991 21.6358 9.34523C21.6358 8.52087 21.5434 8.31853 21.1739 8.31853C21.0617 8.31853 20.9166 8.38598 20.844 8.46841Z"
                  fill="#249B7E"
                />
                <path
                  d="M17.7824 11.0238C17.6174 11.1737 17.6108 11.2112 17.6108 11.9082C17.6108 12.5751 17.624 12.6501 17.7626 12.8C17.9671 13.0173 18.2377 13.0098 18.4356 12.7775C18.5874 12.6126 18.6006 12.5377 18.6006 11.9156C18.6006 11.2936 18.5874 11.2187 18.4356 11.0538C18.2377 10.829 18.0133 10.814 17.7824 11.0238Z"
                  fill="#249B7E"
                />
                <path
                  d="M19.2935 11.7283C19.1417 11.8932 19.1285 11.9681 19.1285 12.5901C19.1285 13.2121 19.1417 13.2871 19.2935 13.4519C19.4914 13.6768 19.7158 13.6918 19.9467 13.4819C20.1117 13.332 20.1183 13.2946 20.1183 12.5976C20.1183 11.9306 20.1051 11.8557 19.9665 11.7058C19.762 11.4885 19.4914 11.496 19.2935 11.7283Z"
                  fill="#249B7E"
                />
                <path
                  d="M20.8572 12.3353C20.7252 12.4402 20.712 12.5076 20.712 13.2271C20.712 13.9765 20.7186 14.0065 20.8836 14.1563C20.9826 14.2463 21.1079 14.3137 21.1739 14.3137C21.2399 14.3137 21.3653 14.2463 21.4642 14.1563C21.6292 14.0065 21.6358 13.9765 21.6358 13.2271C21.6358 12.5076 21.6226 12.4402 21.4906 12.3353C21.4115 12.2678 21.2663 12.2154 21.1739 12.2154C21.0815 12.2154 20.9364 12.2678 20.8572 12.3353Z"
                  fill="#249B7E"
                />
                <path
                  d="M17.756 14.9358C17.6306 15.0932 17.6108 15.1906 17.6108 15.8276C17.6108 16.517 17.6174 16.5545 17.7824 16.7044C18.0001 16.8993 18.2113 16.9067 18.4092 16.7194C18.5544 16.592 18.5676 16.5021 18.5874 15.8576C18.6138 15.1756 18.6072 15.1381 18.4422 14.9508C18.2311 14.711 17.9474 14.7035 17.756 14.9358Z"
                  fill="#249B7E"
                />
                <path
                  d="M19.2605 15.6403C19.1351 15.8277 19.1219 15.9251 19.1417 16.5396C19.1615 17.304 19.2209 17.4239 19.5706 17.5064C19.7422 17.5438 19.8148 17.5139 19.9467 17.364C20.1051 17.1841 20.1183 17.1242 20.1183 16.4497C20.1183 15.8352 20.0985 15.7153 19.9863 15.5879C19.7884 15.363 19.4387 15.3855 19.2605 15.6403Z"
                  fill="#249B7E"
                />
                <path
                  d="M20.8243 16.2473C20.6527 16.4572 20.6659 17.8361 20.8309 18.0084C20.9958 18.1808 21.3917 18.1733 21.5237 18.001C21.6953 17.7911 21.6821 16.4122 21.5171 16.2398C21.3522 16.0675 20.9563 16.075 20.8243 16.2473Z"
                  fill="#249B7E"
                />
                <path
                  d="M17.7428 18.8103C17.6306 18.9377 17.6108 19.0576 17.6108 19.7021C17.6108 20.429 17.6174 20.4515 17.789 20.6089C17.921 20.7288 18.0265 20.7588 18.1717 20.7288C18.5082 20.6464 18.5676 20.519 18.5874 19.7621C18.6072 19.1475 18.594 19.0501 18.4686 18.8628C18.2905 18.608 17.9408 18.5855 17.7428 18.8103Z"
                  fill="#249B7E"
                />
                <path
                  d="M19.2671 19.5298C19.1549 19.6946 19.1285 19.837 19.1285 20.3167C19.1285 21.0361 19.2011 21.2609 19.4387 21.3359C19.9665 21.4932 20.1183 21.2684 20.1183 20.3391C20.1183 19.7321 20.0985 19.6122 19.9863 19.4848C19.7884 19.26 19.4387 19.2825 19.2671 19.5298Z"
                  fill="#249B7E"
                />
                <path
                  d="M20.8243 20.1443C20.6527 20.3541 20.6659 21.7331 20.8309 21.9054C20.9958 22.0778 21.3917 22.0703 21.5237 21.8979C21.6953 21.6881 21.6821 20.3092 21.5171 20.1368C21.3522 19.9644 20.9563 19.9719 20.8243 20.1443Z"
                  fill="#249B7E"
                />
                <path
                  d="M17.7428 22.7073C17.6306 22.8347 17.6108 22.9546 17.6108 23.5391C17.6108 23.9138 17.6438 24.281 17.6834 24.3635C17.8418 24.7082 18.3762 24.6782 18.528 24.311C18.5676 24.2061 18.6006 23.8614 18.6006 23.5391C18.6006 23.0595 18.5742 22.9171 18.462 22.7522C18.2905 22.5049 17.9408 22.4824 17.7428 22.7073Z"
                  fill="#249B7E"
                />
                <path
                  d="M19.3529 23.2918C19.1945 23.4117 19.1285 23.6965 19.1285 24.2435C19.1285 25.0304 19.2407 25.2552 19.6432 25.2552C20.0259 25.2552 20.1183 25.0604 20.1183 24.2585C20.1183 23.4192 20.0391 23.2318 19.6696 23.2318C19.5376 23.2318 19.3925 23.2618 19.3529 23.2918Z"
                  fill="#249B7E"
                />
                <path
                  d="M20.8836 23.9887C20.7186 24.1386 20.712 24.1686 20.712 24.918C20.712 25.6374 20.7252 25.7049 20.8572 25.8098C20.9364 25.8772 21.0815 25.9297 21.1739 25.9297C21.2663 25.9297 21.4115 25.8772 21.4906 25.8098C21.6226 25.7049 21.6358 25.6374 21.6358 24.918C21.6358 24.1686 21.6292 24.1386 21.4642 23.9887C21.3653 23.8988 21.2399 23.8313 21.1739 23.8313C21.1079 23.8313 20.9826 23.8988 20.8836 23.9887Z"
                  fill="#249B7E"
                />
                <path
                  d="M17.7164 26.6192C17.6372 26.7391 17.6108 26.9715 17.6108 27.4661C17.6108 28.2904 17.69 28.4778 18.0595 28.4778C18.495 28.4778 18.6006 28.2754 18.6006 27.4661C18.6006 26.6567 18.495 26.4544 18.0595 26.4544C17.8814 26.4544 17.789 26.4993 17.7164 26.6192Z"
                  fill="#249B7E"
                />
                <path
                  d="M19.4057 27.1288C19.2011 27.2262 19.1285 27.496 19.1285 28.1705C19.1285 28.8899 19.2605 29.1522 19.63 29.1522C20.0259 29.1522 20.1183 28.9649 20.1183 28.148C20.1183 27.5859 20.0919 27.4136 20.0061 27.2937C19.8609 27.1063 19.6102 27.0314 19.4057 27.1288Z"
                  fill="#249B7E"
                />
                <path
                  d="M20.943 27.8182C20.7318 27.9531 20.712 28.0281 20.712 28.7925C20.712 29.6243 20.8044 29.8267 21.1739 29.8267C21.5434 29.8267 21.6358 29.6243 21.6358 28.7925C21.6358 28.0206 21.616 27.9456 21.3917 27.8107C21.2135 27.7058 21.1145 27.7058 20.943 27.8182Z"
                  fill="#249B7E"
                />
                <path
                  d="M26.5845 14.7635C26.4789 14.8834 26.4525 15.0108 26.4525 15.3855C26.4525 15.9626 26.5911 16.1874 26.9474 16.1874C27.3037 16.1874 27.4423 15.9626 27.4423 15.3855C27.4423 14.8159 27.3103 14.6136 26.9474 14.6136C26.8022 14.6136 26.6637 14.6736 26.5845 14.7635Z"
                  fill="#249B7E"
                />
                <path
                  d="M28.1615 15.8051C28.023 15.9326 28.0032 16.015 28.0032 16.4871C28.0032 16.9592 28.023 17.0417 28.1615 17.1691C28.3595 17.3564 28.6366 17.3489 28.8148 17.1616C28.9401 17.0267 28.9599 16.9218 28.9599 16.4871C28.9599 16.0225 28.9467 15.9625 28.7884 15.8201C28.5706 15.6253 28.3595 15.6178 28.1615 15.8051Z"
                  fill="#249B7E"
                />
                <path
                  d="M26.5845 17.3265C26.4789 17.4539 26.4525 17.5887 26.4525 17.9635C26.4525 18.533 26.5845 18.7354 26.9474 18.7354C27.3169 18.7354 27.4423 18.533 27.4423 17.9485C27.4423 17.5588 27.4159 17.4314 27.3103 17.3115C27.1321 17.1091 26.7626 17.1091 26.5845 17.3265Z"
                  fill="#249B7E"
                />
                <path
                  d="M28.2539 18.3008C27.9371 18.4731 27.8778 19.4549 28.1681 19.7246C28.3594 19.9045 28.663 19.897 28.8279 19.7097C28.9335 19.5897 28.9599 19.4623 28.9599 19.0427C28.9599 18.5705 28.9467 18.5106 28.7883 18.3682C28.597 18.1958 28.4716 18.1808 28.2539 18.3008Z"
                  fill="#249B7E"
                />
                <path
                  d="M26.5911 19.9045C26.4855 20.0543 26.4525 20.2117 26.4525 20.534C26.4525 21.0885 26.5911 21.3134 26.9474 21.3134C27.3037 21.3134 27.4423 21.0885 27.4423 20.534C27.4423 19.9869 27.2773 19.7096 26.9474 19.7096C26.789 19.7096 26.6901 19.7621 26.5911 19.9045Z"
                  fill="#249B7E"
                />
                <path
                  d="M28.1285 20.9911C28.0362 21.111 28.0032 21.2609 28.0032 21.6206C28.0032 22.1901 28.1351 22.4075 28.4782 22.4075C28.8346 22.4075 28.9599 22.1976 28.9599 21.5981C28.9599 21.1934 28.9335 21.0585 28.8412 20.9611C28.663 20.7812 28.2803 20.7962 28.1285 20.9911Z"
                  fill="#249B7E"
                />
                <path
                  d="M26.6175 22.4449C26.4789 22.5948 26.4525 22.6997 26.4525 23.0819C26.4525 23.4641 26.4789 23.5691 26.6175 23.7189C26.822 23.9588 27.0728 23.9588 27.2773 23.7189C27.4159 23.5691 27.4423 23.4641 27.4423 23.0819C27.4423 22.6997 27.4159 22.5948 27.2773 22.4449C27.1915 22.34 27.0398 22.2576 26.9474 22.2576C26.855 22.2576 26.7032 22.34 26.6175 22.4449Z"
                  fill="#249B7E"
                />
                <path
                  d="M28.1285 23.5391C28.0362 23.659 28.0032 23.8088 28.0032 24.1686C28.0032 24.7531 28.1285 24.9554 28.498 24.9554C28.8543 24.9554 28.9599 24.7831 28.9599 24.1985C28.9599 23.5765 28.8477 23.3817 28.5046 23.3817C28.3331 23.3817 28.2143 23.4341 28.1285 23.5391Z"
                  fill="#249B7E"
                />
                <path
                  d="M26.6175 24.993C26.4789 25.1429 26.4525 25.2478 26.4525 25.63C26.4525 26.0122 26.4789 26.1172 26.6175 26.267C26.822 26.5069 27.0728 26.5069 27.2773 26.267C27.4159 26.1172 27.4423 26.0122 27.4423 25.63C27.4423 25.2478 27.4159 25.1429 27.2773 24.993C27.1915 24.8881 27.0398 24.8057 26.9474 24.8057C26.855 24.8057 26.7032 24.8881 26.6175 24.993Z"
                  fill="#249B7E"
                />
                <path
                  d="M28.1351 26.0797C27.9899 26.267 27.9305 26.8141 28.0295 27.1438C28.1548 27.571 28.5375 27.6759 28.8477 27.3687C29.0192 27.1963 29.006 26.282 28.8279 26.0797C28.6563 25.8848 28.2934 25.8848 28.1351 26.0797Z"
                  fill="#249B7E"
                />
                <path
                  d="M26.7362 27.4285C26.5317 27.526 26.4525 27.7358 26.4525 28.2004C26.4525 28.725 26.6241 29.0023 26.9474 29.0023C27.2773 29.0023 27.4423 28.725 27.4423 28.178C27.4423 27.8782 27.4093 27.6983 27.3301 27.5934C27.1915 27.4061 26.9342 27.3311 26.7362 27.4285Z"
                  fill="#249B7E"
                />
                <path
                  d="M28.1681 28.6126C27.9635 28.8 27.9042 29.467 28.0625 29.8117C28.2011 30.119 28.5112 30.2014 28.7553 29.9991C28.9071 29.8642 28.9269 29.7892 28.9467 29.3171C28.9599 28.86 28.9467 28.7625 28.8279 28.6276C28.663 28.4403 28.3594 28.4328 28.1681 28.6126Z"
                  fill="#249B7E"
                />
                <path
                  d="M3.06181 16.8993C2.96943 16.9967 2.89685 17.1691 2.89685 17.274C2.89685 17.3789 2.96943 17.5513 3.06181 17.6487C3.20697 17.8211 3.26635 17.8361 3.87339 17.8361C4.66518 17.8361 4.83674 17.7386 4.83674 17.274C4.83674 16.8094 4.66518 16.7119 3.87339 16.7119C3.26635 16.7119 3.20697 16.7269 3.06181 16.8993Z"
                  fill="#249B7E"
                />
                <path
                  d="M5.73409 16.8618C5.52954 17.0941 5.53614 17.4988 5.74729 17.7012C5.85286 17.8136 6.00462 17.8361 6.52588 17.8361C7.07354 17.8361 7.1923 17.8136 7.34406 17.6787C7.5684 17.4763 7.575 17.1466 7.35066 16.8993C7.2055 16.7269 7.14612 16.7119 6.52588 16.7119C5.95183 16.7119 5.84626 16.7344 5.73409 16.8618Z"
                  fill="#249B7E"
                />
                <path
                  d="M8.41297 16.8693C8.18863 17.0717 8.17543 17.4464 8.39317 17.6712C8.53174 17.8211 8.61752 17.8361 9.21796 17.8361C9.98995 17.8361 10.1549 17.7386 10.1549 17.289C10.1549 16.8243 9.96356 16.7119 9.17837 16.7119C8.6835 16.7119 8.56473 16.7344 8.41297 16.8693Z"
                  fill="#249B7E"
                />
                <path
                  d="M3.06177 19.2899C2.9694 19.3648 2.92981 19.4847 2.92981 19.7095C2.92981 20.1742 3.04198 20.2341 3.89315 20.2341C4.57937 20.2341 4.60577 20.2266 4.73773 20.0393C4.81691 19.9269 4.87629 19.7845 4.87629 19.7095C4.87629 19.6346 4.81691 19.4922 4.73773 19.3798C4.60577 19.1924 4.57937 19.185 3.89315 19.185C3.39169 19.185 3.14755 19.2149 3.06177 19.2899Z"
                  fill="#249B7E"
                />
                <path
                  d="M5.70104 19.3348C5.63506 19.4173 5.58228 19.5896 5.58228 19.7095C5.58228 19.8294 5.63506 20.0018 5.70104 20.0842C5.80662 20.2191 5.90559 20.2341 6.53902 20.2341C7.34401 20.2341 7.51556 20.1442 7.51556 19.7095C7.51556 19.2749 7.34401 19.185 6.53902 19.185C5.90559 19.185 5.80662 19.1999 5.70104 19.3348Z"
                  fill="#249B7E"
                />
                <path
                  d="M8.34691 19.3498C8.28752 19.4398 8.24133 19.6046 8.24133 19.7095C8.24133 19.8145 8.28752 19.9793 8.34691 20.0693C8.43928 20.2266 8.49866 20.2341 9.19148 20.2341C10.0361 20.2341 10.1548 20.1667 10.1548 19.6946C10.1548 19.2524 10.0229 19.185 9.17828 19.185C8.49866 19.185 8.43928 19.1999 8.34691 19.3498Z"
                  fill="#249B7E"
                />
                <path
                  d="M3.06181 21.7705C2.96943 21.8679 2.89685 22.0403 2.89685 22.1452C2.89685 22.2501 2.96943 22.4225 3.06181 22.5199C3.20697 22.6923 3.26635 22.7073 3.87339 22.7073C4.66518 22.7073 4.83674 22.6099 4.83674 22.1452C4.83674 21.6806 4.66518 21.5832 3.87339 21.5832C3.26635 21.5832 3.20697 21.5981 3.06181 21.7705Z"
                  fill="#249B7E"
                />
                <path
                  d="M5.74729 21.7106C5.53614 21.9204 5.52954 22.3251 5.73409 22.5574C5.84626 22.6848 5.95183 22.7073 6.52588 22.7073C7.14612 22.7073 7.2055 22.6923 7.35066 22.5199C7.575 22.2726 7.5684 21.9429 7.34406 21.7405C7.1923 21.6056 7.07354 21.5832 6.52588 21.5832C6.00462 21.5832 5.85286 21.6056 5.74729 21.7106Z"
                  fill="#249B7E"
                />
                <path
                  d="M8.3866 21.7555C8.18205 22.0028 8.19525 22.3551 8.41299 22.5499C8.56475 22.6848 8.68352 22.7073 9.17839 22.7073C9.96358 22.7073 10.1549 22.5949 10.1549 22.1227C10.1549 21.6806 9.98997 21.5832 9.21138 21.5832C8.58455 21.5832 8.51856 21.5981 8.3866 21.7555Z"
                  fill="#249B7E"
                />
                <path
                  d="M3.02877 24.2061C2.87701 24.3785 2.86382 24.5659 2.97599 24.8431C3.07496 25.0904 3.18713 25.1129 3.97892 25.0904C4.59916 25.068 4.63875 25.0605 4.76411 24.8656C4.84989 24.7307 4.88288 24.6108 4.84989 24.4834C4.77071 24.1012 4.65854 24.0563 3.87995 24.0563C3.24651 24.0563 3.14754 24.0712 3.02877 24.2061Z"
                  fill="#249B7E"
                />
                <path
                  d="M5.70104 24.206C5.63506 24.2885 5.58228 24.4609 5.58228 24.5808C5.58228 24.7007 5.63506 24.873 5.70104 24.9555C5.80662 25.0904 5.90559 25.1053 6.53902 25.1053C7.34401 25.1053 7.51556 25.0154 7.51556 24.5808C7.51556 24.1461 7.34401 24.0562 6.53902 24.0562C5.90559 24.0562 5.80662 24.0712 5.70104 24.206Z"
                  fill="#249B7E"
                />
                <path
                  d="M8.34696 24.221C8.2018 24.4534 8.21499 24.7756 8.37335 24.9555C8.49212 25.0904 8.59109 25.1053 9.21793 25.1053C10.0361 25.1053 10.1549 25.0379 10.1549 24.5658C10.1549 24.1236 10.0229 24.0562 9.17834 24.0562C8.49872 24.0562 8.43933 24.0712 8.34696 24.221Z"
                  fill="#249B7E"
                />
                <path
                  d="M3.06181 26.6417C2.96943 26.7391 2.89685 26.9115 2.89685 27.0164C2.89685 27.1213 2.96943 27.2937 3.06181 27.3911C3.20697 27.5635 3.26635 27.5785 3.87339 27.5785C4.61899 27.5785 4.77735 27.4961 4.84993 27.0764C4.88292 26.8815 4.85653 26.7991 4.72457 26.6492C4.55961 26.4694 4.51342 26.4544 3.88659 26.4544C3.26635 26.4544 3.20697 26.4694 3.06181 26.6417Z"
                  fill="#249B7E"
                />
                <path
                  d="M5.74729 26.5818C5.53614 26.7916 5.52954 27.1963 5.73409 27.4286C5.84626 27.556 5.95183 27.5785 6.52588 27.5785C7.14612 27.5785 7.2055 27.5635 7.35066 27.3911C7.56181 27.1588 7.5684 26.8366 7.36386 26.6192C7.2253 26.4694 7.13952 26.4544 6.54568 26.4544C6.00462 26.4544 5.85286 26.4769 5.74729 26.5818Z"
                  fill="#249B7E"
                />
                <path
                  d="M8.38653 26.6043C8.17539 26.8291 8.18858 27.2188 8.41292 27.4211C8.56468 27.556 8.68345 27.5785 9.19812 27.5785C9.93052 27.5785 10.1549 27.4436 10.1549 27.0089C10.1549 26.5518 9.9965 26.4544 9.21131 26.4544C8.61747 26.4544 8.5119 26.4769 8.38653 26.6043Z"
                  fill="#249B7E"
                />
                <path
                  d="M3.02873 29.0773C2.85718 29.2722 2.86378 29.527 3.03533 29.7818C3.1739 29.9766 3.18049 29.9841 3.8997 29.9616C4.60572 29.9391 4.63871 29.9316 4.76407 29.7368C4.84985 29.6019 4.88284 29.482 4.84985 29.3546C4.77067 28.9724 4.6585 28.9274 3.87991 28.9274C3.24648 28.9274 3.1475 28.9424 3.02873 29.0773Z"
                  fill="#249B7E"
                />
                <path
                  d="M5.70108 29.0773C5.55592 29.2646 5.55592 29.6468 5.70768 29.8342C5.80665 29.9541 5.91882 29.9766 6.53906 29.9766C7.16589 29.9766 7.26486 29.9616 7.38363 29.8267C7.54199 29.6468 7.55519 29.3246 7.41002 29.0923C7.31765 28.9349 7.25826 28.9274 6.56545 28.9274C5.90562 28.9274 5.80665 28.9424 5.70108 29.0773Z"
                  fill="#249B7E"
                />
                <path
                  d="M8.35355 29.0623C8.20179 29.2496 8.21499 29.6468 8.37335 29.8267C8.48552 29.9541 8.59109 29.9766 9.18493 29.9766C10.0031 29.9766 10.1549 29.8941 10.1549 29.4595C10.1549 28.9948 10.0361 28.9274 9.18493 28.9274C8.5647 28.9274 8.44593 28.9499 8.35355 29.0623Z"
                  fill="#249B7E"
                />
              </svg>
              <p>Subtype</p>
            </div>
            {!isEditing ? (
              <p className="text-end">{property?.unitType || "NA"}</p>
            ) : (
              <>
                {(editData.type === "Residential Rent" ||
                  editData.type === "Residential Sell") && (
                    <select
                      className="border rounded px-2 py-1 text-end"
                      value={editData.unitType}
                      onChange={(e) =>
                        setEditData({ ...editData, unitType: e.target.value })
                      }
                    >
                      <option value="">Select Subtype</option>
                      {residentialSubTypes.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  )}

                {(editData.type === "Commercial Rent" ||
                  editData.type === "Commercial Sell") && (
                    <select
                      className="border rounded px-2 py-1 text-end"
                      value={editData.unitType}
                      onChange={(e) =>
                        setEditData({ ...editData, unitType: e.target.value })
                      }
                    >
                      <option value="">Select Subtype</option>
                      {commercialSubtypes.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  )}

                {/* Fallback if no match */}
                {![
                  "Residential Rent",
                  "Residential Sell",
                  "Commercial Rent",
                  "Commercial Sell",
                ].includes(editData.type) && (
                    <input
                      className="border rounded px-2 py-1 text-end"
                      value={editData.unitType}
                      onChange={(e) =>
                        setEditData({ ...editData, unitType: e.target.value })
                      }
                    />
                  )}
              </>
            )}
          </div>
          <hr className="block"></hr>

          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-row items-center">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 22C11.7667 22 11.5667 21.9333 11.4 21.8C11.2333 21.6667 11.1083 21.4917 11.025 21.275C10.7083 20.3417 10.3083 19.4667 9.825 18.65C9.35833 17.8333 8.7 16.875 7.85 15.775C7 14.675 6.30833 13.625 5.775 12.625C5.25833 11.625 5 10.4167 5 9C5 7.05 5.675 5.4 7.025 4.05C8.39167 2.68333 10.05 2 12 2C13.95 2 15.6 2.68333 16.95 4.05C18.3167 5.4 19 7.05 19 9C19 10.5167 18.7083 11.7833 18.125 12.8C17.5583 13.8 16.9 14.7917 16.15 15.775C15.25 16.975 14.5667 17.975 14.1 18.775C13.65 19.5583 13.275 20.3917 12.975 21.275C12.8917 21.5083 12.7583 21.6917 12.575 21.825C12.4083 21.9417 12.2167 22 12 22ZM12 18.425C12.2833 17.8583 12.6 17.3 12.95 16.75C13.3167 16.2 13.85 15.4667 14.55 14.55C15.2667 13.6167 15.85 12.7583 16.3 11.975C16.7667 11.175 17 10.1833 17 9C17 7.61667 16.5083 6.44167 15.525 5.475C14.5583 4.49167 13.3833 4 12 4C10.6167 4 9.43333 4.49167 8.45 5.475C7.48333 6.44167 7 7.61667 7 9C7 10.1833 7.225 11.175 7.675 11.975C8.14167 12.7583 8.73333 13.6167 9.45 14.55C10.15 15.4667 10.675 16.2 11.025 16.75C11.3917 17.3 11.7167 17.8583 12 18.425ZM12 11.5C12.7 11.5 13.2917 11.2583 13.775 10.775C14.2583 10.2917 14.5 9.7 14.5 9C14.5 8.3 14.2583 7.70833 13.775 7.225C13.2917 6.74167 12.7 6.5 12 6.5C11.3 6.5 10.7083 6.74167 10.225 7.225C9.74167 7.70833 9.5 8.3 9.5 9C9.5 9.7 9.74167 10.2917 10.225 10.775C10.7083 11.2583 11.3 11.5 12 11.5Z"
                  fill="#1D1B20"
                />
              </svg>
              <p>Area</p>
            </div>
            {!isEditing ? (
              <p className="text-end">{property?.area || "NA"}</p>
            ) : (
              <input
                className="border rounded px-2 py-1  text-right"
                value={editData.area}
                onChange={(e) =>
                  setEditData({ ...editData, area: e.target.value })
                }
              />
            )}
          </div>
          <hr className="block"></hr>

          {property?.type !== "Commercial Rent" &&
            property?.type !== "Commercial Sell" && (
              <div>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex flex-row items-center">
                    <FaBed className="text-[#f1d35c] mr-2 h-6 w-6" />
                    <p>Size</p>
                  </div>
                  {!isEditing ? (
                    <p className="text-end">
                      {["Commercial Rent", "Commercial Sell"].includes(editData.type)
                        ? "NA"
                        : editData.bhk || "NA"}
                    </p>
                  ) : (
                    <>
                      {["Residential Rent", "Residential Sell", "Residential"].includes(editData.type) ? (
                        <select
                          className="border rounded px-2 py-1 text-end"
                          value={editData.bhk}
                          onChange={(e) =>
                            setEditData({ ...editData, bhk: e.target.value })
                          }
                        >
                          <option value="">Select BHK</option>
                          {bhkOptions.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p className="text-end text-gray-400">NA</p>
                      )}
                    </>
                  )}

                </div>
                <hr className="block"></hr>
              </div>
            )}

          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-row items-center">
              <TbLamp className="text-[#5f8de9] mr-2 h-6 w-6" />
              <p>Condition</p>
            </div>
            {!isEditing ? (
              <p className="text-end">{editData.furnishedType || "NA"}</p>
            ) : (
              <select
                className="border rounded px-2 py-1 text-end"
                value={editData.furnishedType}
                onChange={(e) =>
                  setEditData({ ...editData, furnishedType: e.target.value })
                }
              >
                <option value="">Select Furnishing</option>
                {furnishingOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Second grid of items */}
        {(showMore || !isMobile) && (
          <div className="grid grid-cols-1 gap-2 pl-0 lg:pl-4">
            <hr className="lg:hidden block"></hr>

            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-row items-center">
                <GoHome className="text-[#247d00] mr-2 h-6 w-6" />
                <p>Address</p>
              </div>
              {!isEditing ? (
                <p className="text-end">{property?.address || "NA"}</p>
              ) : (
                <input
                  className="border rounded px-2 py-1 text-right w-full"
                  value={editData.address}
                  onChange={(e) =>
                    setEditData({ ...editData, address: e.target.value })
                  }
                  placeholder="Enter Address"
                />
              )}

            </div>
            <hr className="block"></hr>

            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-row items-center">
                <RxDimensions className="text-[#f1d35c] mr-2 h-6 w-6" />
                <p>Sqft</p>
              </div>
              {!isEditing ? (
                <p className="text-end">
                  {property?.sqFt || "NA"} sqft
                </p>
              ) : (
                <input
                  type="number"
                  className="border rounded px-2 py-1 text-right"
                  value={editData.sqFt}
                  onChange={(e) =>
                    setEditData({ ...editData, sqFt: e.target.value })
                  }
                  placeholder="Enter SqFt"
                />
              )}

            </div>
            <hr className="block"></hr>

            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-row items-center">
                <IoDocumentTextOutline className="text-[#247d00] mr-2 h-6 w-6" />
                <p>Description</p>
              </div>
              <div className="flex flex-col items-end">
                {!isEditing ? (
                  <div className="text-end">
                    <p>
                      {showFullDescription
                        ? property?.description || "NA"
                        : truncateDescription(property?.description || "NA")}
                    </p>

                    {property?.description &&
                      property.description.length > 100 && (
                        <button
                          onClick={() => setShowFullDescription(!showFullDescription)}
                          className="text-blue-600 hover:underline text-sm mt-1"
                        >
                          {showFullDescription ? "Show Less" : "Show More"}
                        </button>
                      )}
                  </div>
                ) : (
                  <textarea
                    className="border rounded px-2 py-1 text-right w-full min-h-[70px]"
                    value={editData.description}
                    onChange={(e) =>
                      setEditData({ ...editData, description: e.target.value })
                    }
                    placeholder="Enter Description"
                  />
                )}

              </div>
            </div>
            <hr className="block"></hr>

            <div className="lg:flex hidden items-center justify-between gap-4">
              <div className="flex flex-row items-center">
                <MdOutlinePhone className="text-[#503691] mr-2 h-6 w-6" />
                <p>Contact</p>
              </div>
              {showContact && contactInfo ? (
                <div className="flex items-center gap-3">
                  <div className="bg-green-50 border border-green-200 rounded-full px-4 py-2">
                    {!isEditing ? (
                      <p className="text-green-700 font-medium">
                        {contactInfo.name || "NA"} - {contactInfo.number || "NA"} 📍
                      </p>
                    ) : (
                      <div className="flex flex-col items-end gap-2 w-full">

                        {/* Name */}
                        <input
                          type="text"
                          className="border rounded px-2 py-1 w-full text-right"
                          placeholder="Name"
                          value={editData.name}
                          onChange={(e) =>
                            setEditData({ ...editData, name: e.target.value })
                          }
                        />

                        {/* Number */}
                        <input
                          type="text"
                          className="border rounded px-2 py-1 w-full text-right"
                          placeholder="Contact Number"
                          value={editData.number}
                          onChange={(e) =>
                            setEditData({ ...editData, number: e.target.value })
                          }
                        />

                      </div>
                    )}

                  </div>
                  {/* Phone Link */}
                  <a
                    href={`tel:${typeof contactInfo.number === "string" &&
                      contactInfo.number.startsWith("+91")
                      ? contactInfo.number.replace("+91", "").trim()
                      : contactInfo.number || ""
                      }`}
                    className="text-[#503691] hover:text-blue-600 transition-colors"
                    title="Call"
                  >
                    <MdOutlinePhone className="h-6 w-6" />
                  </a>

                  {/* WhatsApp Link */}
                  <a
                    href={`https://wa.me/${typeof contactInfo.number === "string" &&
                      contactInfo.number.startsWith("+91")
                      ? contactInfo.number.replace("+91", "").trim()
                      : contactInfo.number || ""
                      }`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:scale-110 transition-transform"
                    title="WhatsApp"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 448 512"
                      className="h-6 w-6"
                      fill="#25D366"
                    >
                      <path d="M92.1 254.6c0 24.9 7 49.2 20.2 70.1l3.1 5-13.3 48.6L152 365.2l4.8 2.9c20.2 12 43.4 18.4 67.1 18.4h.1c72.6 0 133.3-59.1 133.3-131.8c0-35.2-15.2-68.3-40.1-93.2c-25-25-58-38.7-93.2-38.7c-72.7 0-131.8 59.1-131.9 131.8zM274.8 330c-12.6 1.9-22.4 .9-47.5-9.9c-36.8-15.9-61.8-51.5-66.9-58.7c-.4-.6-.7-.9-.8-1.1c-2-2.6-16.2-21.5-16.2-41c0-18.4 9-27.9 13.2-32.3c.3-.3 .5-.5 .7-.8c3.6-4 7.9-5 10.6-5c2.6 0 5.3 0 7.6 .1c.3 0 .5 0 .8 0c2.3 0 5.2 0 8.1 6.8c1.2 2.9 3 7.3 4.9 11.8c3.3 8 6.7 16.3 7.3 17.6c1 2 1.7 4.3 .3 6.9c-3.4 6.8-6.9 10.4-9.3 13c-3.1 3.2-4.5 4.7-2.3 8.6c15.3 26.3 30.6 35.4 53.9 47.1c4 2 6.3 1.7 8.6-1c2.3-2.6 9.9-11.6 12.5-15.5c2.6-4 5.3-3.3 8.9-2s23.1 10.9 27.1 12.9c.8 .4 1.5 .7 2.1 1c2.8 1.4 4.7 2.3 5.5 3.6c.9 1.9 .9 9.9-2.4 19.1c-3.3 9.3-19.1 17.7-26.7 18.8zM448 96c0-35.3-28.7-64-64-64H64C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V96zM148.1 393.9L64 416l22.5-82.2c-13.9-24-21.2-51.3-21.2-79.3C65.4 167.1 136.5 96 223.9 96c42.4 0 82.2 16.5 112.2 46.5c29.9 30 47.9 69.8 47.9 112.2c0 87.4-72.7 158.5-160.1 158.5c-26.6 0-52.7-6.7-75.8-19.3z" />
                    </svg>
                  </a>
                </div>
              ) : (
                <button
                  className="bg-[#503691] text-white px-4 py-2 rounded-full hover:bg-blue-600 transition-colors"
                  onClick={handleContactClick}
                >
                  Get Contact Number
                </button>
              )}

            </div>
          </div>
        )}
      </div>

      {/* Show More/Less Button */}
      <div className="flex lg:hidden justify-start cursor-pointer text-md mb-2">
        <p
          onClick={() => setShowMore(!showMore)}
          className="text-blue-800 font-bold"
        >
          {isMobile && showMore ? "Show Less" : "Show More..."}
        </p>
      </div>

      {/* Mobile Action Buttons */}
      <div className="flex flex-col lg:flex-2 lg:hidden justify-between gap-2 w-full">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 w-full">
          {/* Share */}
          <div className="flex flex-row items-center justify-center gap-2 h-fit hover:bg-white bg-[#503691] border border-[#503691] text-white hover:text-[#503691] px-4 md:px-6 py-1.5 rounded-lg cursor-pointer text-sm md:text-base" onClick={handleShareClick}>
            <FaShareAlt className="h-4 w-4" />
            Share
          </div>

          {/* Photos */}
          <div className="flex flex-row items-center justify-center gap-2 h-fit bg-gray-100 hover:bg-white border border-gray-300 text-gray-400 hover:text-[#503691] px-4 md:px-6 py-1.5 rounded-lg cursor-pointer text-sm md:text-base">
            <FaCameraRetro className="h-4 w-4 text-gray-400" />
            Photos
          </div>

          {/* Edit */}
          <div
            className="flex flex-row items-center justify-center gap-2 h-fit bg-white text-[#503691] hover:bg-[#503691] hover:text-white border border-[#503691] px-4 md:px-6 py-1.5 rounded-lg cursor-pointer text-sm md:text-base"
            onClick={() => {
              setIsEditing(!isEditing);
            }}
          >
            <MdNoteAlt className="h-4 w-4" />
            {isEditing ? "Close" : "Edit"}
          </div>

          {isEditing && (
            <button
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              onClick={handleSaveProperty}
            >
              Save
            </button>
          )}

          {/* Delete */}
          <div
            className="flex flex-row items-center justify-center gap-2 h-fit bg-white text-red-600 hover:bg-red-600 hover:text-white border border-red-600 px-4 md:px-6 py-1.5 rounded-lg cursor-pointer text-sm md:text-base"
            onClick={() => setShowDeleteModal(true)}
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </div>
        </div>
      </div>

      {/* Mobile Contact Button */}
      <div className="flex lg:hidden items-center justify-between mt-3 gap-4 px-4 pb-4">
        {showContact && contactInfo ? (
          <div className="w-full flex items-center gap-3">
            {/* Contact Info */}
            <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex-1">
              {!isEditing ? (
                <p className="text-green-700 font-medium">
                  {contactInfo.name || "NA"} - {contactInfo.number || "NA"} 📍
                </p>
              ) : (
                <div className="flex flex-col items-end gap-2 w-full">

                  {/* Name */}
                  <input
                    type="text"
                    className="border rounded px-2 py-1 w-full text-right"
                    placeholder="Name"
                    value={editData.name}
                    onChange={(e) =>
                      setEditData({ ...editData, name: e.target.value })
                    }
                  />

                  {/* Number */}
                  <input
                    type="text"
                    className="border rounded px-2 py-1 w-full text-right"
                    placeholder="Contact Number"
                    value={editData.number}
                    onChange={(e) =>
                      setEditData({ ...editData, number: e.target.value })
                    }
                  />

                </div>
              )}
            </div>

            {/* Action Buttons - Simple Same Line */}
            <div className="flex gap-2">
              <a
                href={`tel:${contactInfo.number}`}
                className="p-2 bg-blue-50 rounded-lg text-[#503691] hover:bg-blue-100 transition-colors"
                title="Call"
              >
                <MdOutlinePhone className="h-5 w-5" />
              </a>
              <a
                href={`https://wa.me/${contactInfo.number}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                title="WhatsApp"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 448 512"
                  className="h-5 w-5"
                  fill="#25D366"
                >
                  <path d="M92.1 254.6c0 24.9 7 49.2 20.2 70.1l3.1 5-13.3 48.6L152 365.2l4.8 2.9c20.2 12 43.4 18.4 67.1 18.4h.1c72.6 0 133.3-59.1 133.3-131.8c0-35.2-15.2-68.3-40.1-93.2c-25-25-58-38.7-93.2-38.7c-72.7 0-131.8 59.1-131.9 131.8zM274.8 330c-12.6 1.9-22.4 .9-47.5-9.9c-36.8-15.9-61.8-51.5-66.9-58.7c-.4-.6-.7-.9-.8-1.1c-2-2.6-16.2-21.5-16.2-41c0-18.4 9-27.9 13.2-32.3c.3-.3 .5-.5 .7-.8c3.6-4 7.9-5 10.6-5c2.6 0 5.3 0 7.6 .1c.3 0 .5 0 .8 0c2.3 0 5.2 0 8.1 6.8c1.2 2.9 3 7.3 4.9 11.8c3.3 8 6.7 16.3 7.3 17.6c1 2 1.7 4.3 .3 6.9c-3.4 6.8-6.9 10.4-9.3 13c-3.1 3.2-4.5 4.7-2.3 8.6c15.3 26.3 30.6 35.4 53.9 47.1c4 2 6.3 1.7 8.6-1c2.3-2.6 9.9-11.6 12.5-15.5c2.6-4 5.3-3.3 8.9-2s23.1 10.9 27.1 12.9c.8 .4 1.5 .7 2.1 1c2.8 1.4 4.7 2.3 5.5 3.6c.9 1.9 .9 9.9-2.4 19.1c-3.3 9.3-19.1 17.7-26.7 18.8zM448 96c0-35.3-28.7-64-64-64H64C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V96zM148.1 393.9L64 416l22.5-82.2c-13.9-24-21.2-51.3-21.2-79.3C65.4 167.1 136.5 96 223.9 96c42.4 0 82.2 16.5 112.2 46.5c29.9 30 47.9 69.8 47.9 112.2c0 87.4-72.7 158.5-160.1 158.5c-26.6 0-52.7-6.7-75.8-19.3z" />
                </svg>
              </a>
            </div>
          </div>
        ) : (
          <button
            className="w-full px-4 py-3 rounded-lg hover:bg-white bg-[#503691] border border-[#503691] text-white hover:text-[#503691] transition-colors text-sm sm:text-base font-medium"
            onClick={handleContactClick}
          >
            Get Contact Number
          </button>
        )}
      </div>
    </div>
  );
};

export default MyProperty;

